import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Play, 
  Users, 
  CreditCard, 
  Crown, 
  Star, 
  Check, 
  X,
  Menu,
  LogOut,
  User,
  Settings,
  PlayCircle,
  Calendar,
  Shield,
  Monitor,
  Trash2,
  Eye,
  EyeOff,
  BarChart3,
  UserCheck,
  UserX,
  UserPlus,
  Search,
  Key,
  DollarSign,
  Clock,
  Mail,
  Ban
} from "lucide-react";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData, temp_password_required } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Only set user if password change is NOT required
      if (temp_password_required) {
        // Return data for password change modal, but don't authenticate yet
        return { 
          success: true, 
          temp_password_required: true,
          userData: userData,
          access_token: access_token
        };
      }
      
      // Normal login flow - authenticate user
      setUser(userData);
      toast.success("Login realizado com sucesso!");
      return { success: true, temp_password_required: false };
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro no login");
      return { success: false };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      toast.success("Conta criada com sucesso!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro no registro");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success("Logout realizado com sucesso!");
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, fetchCurrentUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Components
const Navbar = () => {
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <nav className="bg-slate-900/95 backdrop-blur-lg border-b border-slate-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-pink-600 rounded-lg flex items-center justify-center">
                <Play className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">AnhomeWeb</span>
            </div>
          </div>

          {user && (
            <div className="hidden md:flex items-center space-x-6">
              <div className="flex items-center space-x-2 px-4 py-2 bg-slate-800 rounded-lg">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-white text-sm">{user.name}</span>
                <div className={`w-2 h-2 rounded-full ${user.subscription_status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
              </div>
              <button
                onClick={logout}
                className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sair</span>
              </button>
            </div>
          )}

          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-slate-400 hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>

        {isMenuOpen && user && (
          <div className="md:hidden bg-slate-800 rounded-lg mt-2 p-4">
            <div className="flex items-center space-x-2 mb-4">
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-white">{user.name}</span>
            </div>
            <button
              onClick={() => {
                logout();
                setIsMenuOpen(false);
              }}
              className="flex items-center space-x-2 text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
              <span>Sair</span>
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

const AuthForm = () => {
  const { login, register, fetchCurrentUser } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  
  // New states for password change requirement
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [tempUserData, setTempUserData] = useState(null); // Store user data temporarily

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let result = false;
    if (isLogin) {
      result = await login(formData.email, formData.password);
      
      // Check if password change is required
      if (result && result.success && result.temp_password_required) {
        setPasswordChangeData({
          current_password: formData.password, // Store temp password as current
          new_password: '',
          confirm_password: ''
        });
        setTempUserData(result.userData); // Store user data to authenticate after password change
        setShowPasswordChangeModal(true);
        toast.warning('Você precisa alterar sua senha temporária antes de continuar');
      }
    } else {
      result = await register(formData.name, formData.email, formData.password);
    }

    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Digite seu email');
      return;
    }

    setForgotLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotEmail
      });

      if (response.data.success) {
        setTempPassword('enviado'); // Flag para mostrar confirmação
        toast.success('Senha temporária enviada por email!');
      }
    } catch (error) {
      console.error('Error in forgot password:', error);
      toast.error(error.response?.data?.detail || 'Erro ao recuperar senha');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setTempPassword('');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!passwordChangeData.new_password || !passwordChangeData.confirm_password) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    if (passwordChangeData.new_password.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (passwordChangeData.new_password !== passwordChangeData.confirm_password) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setPasswordChangeLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordChangeData.current_password,
        new_password: passwordChangeData.new_password
      });
      
      toast.success('Senha alterada com sucesso!');
      
      // Authenticate user after successful password change
      await fetchCurrentUser();
      
      // Close modal and reset state
      setShowPasswordChangeModal(false);
      setPasswordChangeData({ current_password: '', new_password: '', confirm_password: '' });
      setTempUserData(null);
      
      toast.success('Login realizado com sucesso!');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800/50 backdrop-blur-lg p-8 rounded-2xl border border-slate-700 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-pink-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Play className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {isLogin ? 'Fazer Login' : 'Criar Conta'}
          </h1>
          <p className="text-slate-400">
            {isLogin ? 'Acesse sua conta AnhomeWeb' : 'Junte-se ao AnhomeWeb hoje'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Nome Completo
              </label>
              <input
                type="text"
                required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Seu nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Email
            </label>
            <input
              type="email"
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="seu@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Senha
            </label>
            <input
              type="password"
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Sua senha"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processando...' : (isLogin ? 'Entrar' : 'Criar Conta')}
          </button>
        </form>

        {/* Esqueceu a senha - apenas no modo login */}
        {isLogin && (
          <div className="text-center mb-4">
            <button
              onClick={() => setShowForgotPassword(true)}
              className="text-orange-500 hover:text-orange-400 text-sm font-medium"
            >
              Esqueceu a senha?
            </button>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-slate-400">
            {isLogin ? 'Não tem uma conta?' : 'Já tem uma conta?'}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="ml-2 text-orange-500 hover:text-orange-400 font-medium"
            >
              {isLogin ? 'Criar conta' : 'Fazer login'}
            </button>
          </p>
        </div>
      </div>

      {/* Modal Esqueceu a Senha */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-8 rounded-xl max-w-md w-full mx-4 border border-slate-700">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white mb-2">Recuperar Senha</h3>
              <p className="text-slate-400 text-sm">
                {tempPassword ? 'Sua senha temporária foi gerada!' : 'Digite seu email para gerar uma nova senha'}
              </p>
            </div>

            {!tempPassword ? (
              <form onSubmit={handleForgotPassword}>
                <div className="mb-6">
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-orange-500"
                    placeholder="seu@email.com"
                    required
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={closeForgotPassword}
                    className="flex-1 bg-slate-600 hover:bg-slate-700 text-white py-3 rounded-lg font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-pink-700 transition-all duration-200 disabled:opacity-50"
                  >
                    {forgotLoading ? 'Gerando...' : 'Gerar Senha'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-center">
                <div className="bg-green-900/20 border border-green-500/30 p-6 rounded-lg mb-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-green-500/20 rounded-full p-3">
                      <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-green-400 font-medium mb-2">📧 Email Enviado!</p>
                  <p className="text-slate-300 text-sm">
                    Sua senha temporária foi enviada para <strong>{forgotEmail}</strong>
                  </p>
                </div>

                <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg mb-6">
                  <p className="text-blue-400 text-sm">
                    ⚠️ <strong>Importante:</strong>
                  </p>
                  <ul className="text-slate-300 text-sm mt-2 text-left space-y-1">
                    <li>• Verifique sua caixa de entrada (e spam)</li>
                    <li>• Use a senha do email para fazer login</li>
                    <li>• Você será solicitado a criar nova senha</li>
                    <li>• A senha temporária expira em 24 horas</li>
                  </ul>
                </div>

                <button
                  onClick={closeForgotPassword}
                  className="w-full bg-gradient-to-r from-orange-500 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-pink-700 transition-all duration-200"
                >
                  Entendi, fazer login
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Modal de Mudança de Senha Obrigatória */}
      {showPasswordChangeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-8 rounded-xl max-w-md w-full mx-4 border border-orange-500/50">
            <div className="text-center mb-6">
              <div className="bg-orange-500/20 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Alterar Senha Temporária</h3>
              <p className="text-slate-400 text-sm">
                Por segurança, você precisa criar uma nova senha permanente
              </p>
            </div>

            <form onSubmit={handlePasswordChange}>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Nova Senha
                  </label>
                  <input
                    type="password"
                    value={passwordChangeData.new_password}
                    onChange={(e) => setPasswordChangeData({...passwordChangeData, new_password: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-orange-500"
                    placeholder="Digite sua nova senha"
                    required
                    minLength={6}
                  />
                  <p className="text-slate-500 text-xs mt-1">Mínimo de 6 caracteres</p>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Confirmar Nova Senha
                  </label>
                  <input
                    type="password"
                    value={passwordChangeData.confirm_password}
                    onChange={(e) => setPasswordChangeData({...passwordChangeData, confirm_password: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-orange-500"
                    placeholder="Digite a senha novamente"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-500/30 p-4 rounded-lg mb-6">
                <p className="text-yellow-400 text-sm flex items-start">
                  <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>
                    <strong>Importante:</strong> Escolha uma senha forte que você consiga lembrar. Você não poderá acessar o sistema sem alterar a senha temporária.
                  </span>
                </p>
              </div>

              <button
                type="submit"
                disabled={passwordChangeLoading}
                className="w-full bg-gradient-to-r from-orange-500 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-pink-700 transition-all duration-200 disabled:opacity-50"
              >
                {passwordChangeLoading ? 'Alterando...' : 'Alterar Senha'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para configuração do Stripe
const StripeConfiguration = () => {
  const [products, setProducts] = useState([]);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchStripeProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/stripe/products`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setProducts(response.data.products);
    } catch (error) {
      console.error('Erro ao carregar produtos Stripe:', error);
      toast.error('Erro ao carregar produtos do Stripe');
    } finally {
      setLoading(false);
    }
  };

  const fetchMapping = async () => {
    try {
      const response = await axios.get(`${API}/admin/stripe/mapping`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setMapping(response.data.mapping);
    } catch (error) {
      console.error('Erro ao carregar mapeamento:', error);
    }
  };

  const saveMapping = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/stripe/map-products`, mapping, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast.success('Mapeamento salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar mapeamento:', error);
      toast.error('Erro ao salvar mapeamento');
    } finally {
      setSaving(false);
    }
  };

  const handleMappingChange = (planType, priceId) => {
    setMapping(prev => ({
      ...prev,
      [planType]: priceId
    }));
  };

  useEffect(() => {
    fetchStripeProducts();
    fetchMapping();
  }, []);

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white">Configuração Stripe</h2>
            <p className="text-slate-400 text-sm">Configure o mapeamento dos produtos do Stripe para os planos do site</p>
          </div>
          <div className="space-x-2">
            <button
              onClick={fetchStripeProducts}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              disabled={loading}
            >
              {loading ? 'Carregando...' : 'Atualizar Produtos'}
            </button>
            <button
              onClick={saveMapping}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              disabled={saving}
            >
              {saving ? 'Salvando...' : 'Salvar Configuração'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            <p className="text-slate-400 mt-2">Carregando produtos do Stripe...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Produtos encontrados */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Produtos Disponíveis no Stripe</h3>
              {products.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-400">Nenhum produto encontrado no Stripe</p>
                  <p className="text-slate-500 text-sm mt-2">
                    Certifique-se de criar produtos recorrentes no Stripe Dashboard primeiro
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map(product => (
                    <div key={product.id} className="bg-slate-700/50 p-4 rounded-lg border border-slate-600">
                      <h4 className="text-white font-medium mb-2">{product.name}</h4>
                      <p className="text-slate-400 text-sm mb-3">{product.description || 'Sem descrição'}</p>
                      
                      {product.prices.map(price => (
                        <div key={price.id} className="bg-slate-800/50 p-3 rounded mb-2">
                          <div className="flex justify-between items-center">
                            <span className="text-white font-medium">
                              R$ {(price.unit_amount / 100).toFixed(2)}
                            </span>
                            <span className="text-slate-400 text-xs">
                              {price.billing_period === 'month' ? 'Mensal' : 'Único'}
                            </span>
                          </div>
                          <p className="text-slate-500 text-xs mt-1">ID: {price.id}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mapeamento dos planos principais */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Mapeamento dos Planos Principais</h3>
              <div className="space-y-4">
                {[
                  { key: 'channels', name: 'Plano Canais (IPTV)' },
                  { key: 'movies_series_anime', name: 'Plano Filmes, Séries e Animes' },
                  { key: 'combo', name: 'Plano Combo' },
                  { 
                    key: 'upgrade_to_combo', 
                    name: 'Upgrade para Combo', 
                    description: 'Para usuários que já têm um plano e querem upgrade (R$ 15,00)' 
                  }
                ].map(plan => (
                  <div key={plan.key} className="bg-slate-700/30 p-4 rounded-lg">
                    <label className="block text-white font-medium mb-2">{plan.name}</label>
                    {plan.description && (
                      <p className="text-slate-400 text-sm mb-3">{plan.description}</p>
                    )}
                    <select
                      value={mapping[plan.key] || ''}
                      onChange={(e) => handleMappingChange(plan.key, e.target.value)}
                      className="w-full bg-slate-800 text-white border border-slate-600 rounded-lg px-3 py-2"
                    >
                      <option value="">Selecione um produto/preço</option>
                      {products.map(product => 
                        product.prices.filter(price => price.recurring && price.billing_period === 'month').map(price => (
                          <option key={price.id} value={price.id}>
                            {product.name} - R$ {(price.unit_amount / 100).toFixed(2)}/mês
                          </option>
                        ))
                      )}
                    </select>
                    {mapping[plan.key] && (
                      <p className="text-green-400 text-sm mt-2">
                        ✅ Configurado: {mapping[plan.key]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Mapeamento das telas extras */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Mapeamento das Telas Extras</h3>
              <div className="space-y-4">
                {[
                  { 
                    key: 'extra_screen_individual_channels', 
                    name: 'Tela Extra - Plano Individual Canais', 
                    description: 'Para usuários com plano Canais - Tela adicional (R$ 15,00)' 
                  },
                  { 
                    key: 'extra_screen_individual_movies', 
                    name: 'Tela Extra - Plano Individual Filmes/Séries', 
                    description: 'Para usuários com plano Filmes/Séries - Tela adicional (R$ 15,00)' 
                  },
                  { 
                    key: 'extra_screen_combo_channels', 
                    name: 'Tela Extra - Plano Combo Canais', 
                    description: 'Para usuários com plano Combo - Tela adicional para Canais (R$ 10,00)' 
                  },
                  { 
                    key: 'extra_screen_combo_movies', 
                    name: 'Tela Extra - Plano Combo Filmes/Séries', 
                    description: 'Para usuários com plano Combo - Tela adicional para Filmes/Séries (R$ 10,00)' 
                  }
                ].map(extraScreen => (
                  <div key={extraScreen.key} className="bg-slate-700/30 p-4 rounded-lg">
                    <label className="block text-white font-medium mb-2">{extraScreen.name}</label>
                    <p className="text-slate-400 text-sm mb-3">{extraScreen.description}</p>
                    <select
                      value={mapping[extraScreen.key] || ''}
                      onChange={(e) => handleMappingChange(extraScreen.key, e.target.value)}
                      className="w-full bg-slate-800 text-white border border-slate-600 rounded-lg px-3 py-2"
                    >
                      <option value="">Selecione um produto/preço</option>
                      {products.map(product => 
                        product.prices.filter(price => price.recurring && price.billing_period === 'month').map(price => (
                          <option key={price.id} value={price.id}>
                            {product.name} - R$ {(price.unit_amount / 100).toFixed(2)}/mês
                          </option>
                        ))
                      )}
                    </select>
                    {mapping[extraScreen.key] && (
                      <p className="text-green-400 text-sm mt-2">
                        ✅ Configurado: {mapping[extraScreen.key]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Status da configuração */}
            <div className="bg-slate-700/30 p-4 rounded-lg">
              <h4 className="text-white font-medium mb-2">Status da Configuração</h4>
              
              {/* Planos Principais */}
              <div className="mb-4">
                <h5 className="text-slate-300 text-sm font-medium mb-2">Planos Principais</h5>
                <div className="space-y-2">
                  {[
                    { key: 'channels', name: 'Canais' },
                    { key: 'movies_series_anime', name: 'Filmes/Séries' },
                    { key: 'combo', name: 'Combo' },
                    { key: 'upgrade_to_combo', name: 'Upgrade para Combo' }
                  ].map(plan => (
                    <div key={plan.key} className="flex items-center space-x-2">
                      {mapping[plan.key] ? (
                        <span className="text-green-400">✅</span>
                      ) : (
                        <span className="text-red-400">❌</span>
                      )}
                      <span className="text-white">{plan.name}</span>
                      {!mapping[plan.key] && (
                        <span className="text-red-400 text-sm">(Não configurado)</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Telas Extras */}
              <div>
                <h5 className="text-slate-300 text-sm font-medium mb-2">Telas Extras</h5>
                <div className="space-y-2">
                  {[
                    { key: 'extra_screen_individual_channels', name: 'Telas Extras Individuais Canais' },
                    { key: 'extra_screen_individual_movies', name: 'Telas Extras Individuais Filmes/Séries' },
                    { key: 'extra_screen_combo_channels', name: 'Telas Extras Combo Canais' },
                    { key: 'extra_screen_combo_movies', name: 'Telas Extras Combo Filmes/Séries' }
                  ].map(extraScreen => (
                    <div key={extraScreen.key} className="flex items-center space-x-2">
                      {mapping[extraScreen.key] ? (
                        <span className="text-green-400">✅</span>
                      ) : (
                        <span className="text-red-400">❌</span>
                      )}
                      <span className="text-white">{extraScreen.name}</span>
                      {!mapping[extraScreen.key] && (
                        <span className="text-red-400 text-sm">(Não configurado)</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Status geral */}
              {(() => {
                const requiredKeys = ['channels', 'movies_series_anime', 'combo', 'upgrade_to_combo', 'extra_screen_individual_channels', 'extra_screen_individual_movies', 'extra_screen_combo_channels', 'extra_screen_combo_movies'];
                const configuredCount = requiredKeys.filter(key => mapping[key]).length;
                const allConfigured = configuredCount === requiredKeys.length;
                
                return (
                  <div className="mt-4">
                    {allConfigured ? (
                      <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <p className="text-green-400 text-sm">
                          ✅ Todos os produtos estão configurados! ({configuredCount}/{requiredKeys.length})
                        </p>
                        <p className="text-green-300 text-xs mt-1">
                          Planos, upgrade e telas extras - assinaturas recorrentes funcionarão para todos os cenários.
                        </p>
                      </div>
                    ) : (
                      <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                        <p className="text-yellow-400 text-sm">
                          ⚠️ Configuração incompleta: {configuredCount}/{requiredKeys.length} produtos configurados
                        </p>
                        <p className="text-yellow-300 text-xs mt-1">
                          Configure todos os produtos (planos, upgrade, telas extras) para funcionamento completo.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, fetchCurrentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTutorialPlan, setSelectedTutorialPlan] = useState('');
  const [availableTutorials, setAvailableTutorials] = useState({});
  const [loadingTutorials, setLoadingTutorials] = useState(false);
  const [informativeSettings, setInformativeSettings] = useState(null);

  useEffect(() => {
    fetchCurrentUser();
    fetchInformativeSettings();
  }, []);

  const fetchInformativeSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/informative-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInformativeSettings(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações informativas:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-500';
      case 'inactive': return 'text-red-500';
      case 'cancelled': return 'text-yellow-500';
      default: return 'text-slate-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Ativo';
      case 'inactive': return 'Inativo';
      case 'cancelled': return 'Cancelado';
      default: return 'Desconhecido';
    }
  };

  const getPlanText = (plan) => {
    switch (plan) {
      case 'channels': return 'Plano Canais';
      case 'movies_series_anime': return 'Filmes, Séries e Animes';
      case 'combo': return 'Combo (Canais + Filmes/Séries)';
      default: return 'Nenhum plano';
    }
  };

  // Funções para tutoriais
  const fetchTutorials = async () => {
    setLoadingTutorials(true);
    try {
      const response = await axios.get(`${API}/tutorials`);
      
      // Organizar tutoriais por plano e dispositivo
      const organized = {
        iptv: {},
        filmes_series: {}
      };
      
      const deviceTypes = [
        { key: 'mobile_android', name: 'Mobile Android' },
        { key: 'mobile_apple', name: 'Mobile Apple' },
        { key: 'tv_samsung', name: 'TV Samsung' },
        { key: 'tv_lg', name: 'TV LG' },
        { key: 'tv_android_tv', name: 'TV Android TV' },
        { key: 'tv_box', name: 'TV BOX' },
        { key: 'apple_tv', name: 'Apple TV' },
        { key: 'fire_stick_amazon', name: 'Fire Stick Amazon' },
        { key: 'xiaomi_mi_stick', name: 'Xiaomi Mi Stick' },
        { key: 'roku_tv', name: 'Roku TV' },
        { key: 'game_xbox', name: 'Game XBOX' },
        { key: 'pc', name: 'PC' }
      ];
      
      // Inicializar estrutura
      deviceTypes.forEach(device => {
        organized.iptv[device.key] = null;
        organized.filmes_series[device.key] = null;
      });
      
      // Preencher com tutoriais disponíveis
      response.data.tutorials.forEach(tutorial => {
        if (organized[tutorial.plan_type]) {
          organized[tutorial.plan_type][tutorial.device_type] = tutorial;
        }
      });
      
      setAvailableTutorials(organized);
    } catch (error) {
      console.error('Erro ao carregar tutoriais:', error);
      toast.error('Erro ao carregar tutoriais');
    } finally {
      setLoadingTutorials(false);
    }
  };

  const downloadTutorial = async (tutorialId, filename) => {
    try {
      const userToken = localStorage.getItem('token');
      
      if (!userToken) {
        toast.error('Você precisa estar logado para baixar tutoriais');
        return;
      }
      
      const response = await axios.get(`${API}/tutorials/download/${tutorialId}`, {
        headers: {
          Authorization: `Bearer ${userToken}`
        },
        responseType: 'blob',
        timeout: 30000  // 30 segundos de timeout
      });
      
      // Verificar se recebeu dados
      if (!response.data || response.data.size === 0) {
        throw new Error('Arquivo vazio recebido');
      }
      
      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Tutorial baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar tutorial:', error);
      if (error.response?.status === 403) {
        toast.error('Você não tem acesso a este tutorial');
      } else if (error.response?.status === 404) {
        toast.error('Tutorial não encontrado');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Tempo limite excedido. Tente novamente');
      } else {
        toast.error('Erro ao baixar tutorial. Tente novamente');
      }
    }
  };

  // Carregar tutoriais quando entrar na aba
  useEffect(() => {
    if (activeTab === 'tutorials' && user) {
      fetchTutorials();
    }
  }, [activeTab, user]);

  if (!user) return <div>Carregando...</div>;

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Bem-vindo ao AnhomeWeb, {user.name}!
          </h1>
          <p className="text-slate-400">
            Gerencie sua assinatura e acesse seus conteúdos favoritos
          </p>
        </div>

        {/* Notificação Informativa do Emby */}
        {(user.subscription_plan === 'movies_series_anime' || user.subscription_plan === 'combo') && (
          <div className="mb-6 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <PlayCircle className="w-6 h-6 text-blue-400 mt-1" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-2 flex items-center">
                  <span className="mr-2">🎬</span>
                  Acesso ao Aplicativo de Filmes e Séries
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  <strong>Importante:</strong> O email e senha utilizados para acessar nosso site <span className="text-blue-400 font-medium">anhomeweb.com</span> são os mesmos para acessar o aplicativo de <strong>Filmes e Séries (Emby)</strong>. 
                  Use suas credenciais atuais para fazer login em ambas as plataformas.
                </p>
                <div className="mt-3 flex items-center space-x-4">
                  <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded-full border border-blue-500/20">
                    📧 Mesmo email
                  </span>
                  <span className="text-xs text-purple-400 bg-purple-500/10 px-2 py-1 rounded-full border border-purple-500/20">
                    🔑 Mesma senha
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          {/* Status da Assinatura */}
          <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
            <div className="flex items-center space-x-3 mb-4">
              <div className={`w-3 h-3 rounded-full ${user.subscription_status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <h3 className="text-lg font-semibold text-white">Status do Plano</h3>
            </div>
            <p className={`text-xl font-bold ${getStatusColor(user.subscription_status)}`}>
              {getStatusText(user.subscription_status)}
            </p>
          </div>

          {/* Plano Atual */}
          <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
            <div className="flex items-center space-x-3 mb-4">
              <Crown className="w-5 h-5 text-orange-500" />
              <h3 className="text-lg font-semibold text-white">Plano</h3>
            </div>
            <p className="text-white font-medium">
              {getPlanText(user.subscription_plan)}
            </p>
          </div>

          {/* Telas Extra */}
          <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
            <div className="flex items-center space-x-3 mb-4">
              <Monitor className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold text-white">Telas</h3>
            </div>
            <div className="text-white">
              <p className="font-medium">{1 + (user.extra_screens || 0)} telas totais</p>
              <p className="text-sm text-slate-400 mt-1">1 padrão + {user.extra_screens || 0} extra{(user.extra_screens || 0) !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {/* Próxima Cobrança */}
          <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
            <div className="flex items-center space-x-3 mb-4">
              <Calendar className="w-5 h-5 text-purple-500" />
              <h3 className="text-lg font-semibold text-white">Próxima Cobrança</h3>
            </div>
            <p className="text-white font-medium">
              {formatDate(user.next_billing_date)}
            </p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6 overflow-x-auto">
          <nav className="flex space-x-4 md:space-x-8 min-w-max md:min-w-0">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              Visão Geral
            </button>
            <button
              onClick={() => setActiveTab('tutorials')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'tutorials'
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              Tutoriais de Instalações
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'subscription'
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              Assinatura
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'payments'
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              Pagamentos
            </button>
            <button
              onClick={() => setActiveTab('account')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'account'
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              Configurações
            </button>
          </nav>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
              <h2 className="text-xl font-semibold text-white mb-4">Resumo da Conta</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-slate-400 mb-2">Email</h3>
                  <p className="text-white">{user.email}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-slate-400 mb-2">Nome</h3>
                  <p className="text-white">{user.name}</p>
                </div>
              </div>
            </div>

            {/* Informativos */}
            {informativeSettings && user.subscription_status === 'active' && (
              <div className="space-y-4">
                {/* Convite para Telegram */}
                <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 p-6 rounded-xl">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <svg className="w-12 h-12 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.122.1.155.235.171.329.016.095.037.312.021.481z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">
                        Junte-se ao nosso Grupo do Telegram!
                      </h3>
                      <p className="text-slate-300 mb-4">
                        Entre no nosso grupo para acompanhar notícias, novidades e fazer solicitações de Filmes, Séries e Animes.
                      </p>
                      <a
                        href={informativeSettings.telegram_group_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-all"
                      >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.122.1.155.235.171.329.016.095.037.312.021.481z"/>
                        </svg>
                        Entrar no Grupo
                      </a>
                    </div>
                  </div>
                </div>

                {/* Informativo baseado no plano do usuário */}
                {(user.subscription_plan === 'combo' || user.subscription_plan === 'movies_series_anime') && informativeSettings.informative_filmes_text && (
                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 p-6 rounded-xl">
                    <div className="whitespace-pre-wrap text-slate-200 text-sm leading-relaxed">
                      {informativeSettings.informative_filmes_text}
                    </div>
                  </div>
                )}

                {user.subscription_plan === 'channels' && informativeSettings.informative_tv_text && (
                  <div className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/20 p-6 rounded-xl">
                    <div className="whitespace-pre-wrap text-slate-200 text-sm leading-relaxed">
                      {informativeSettings.informative_tv_text}
                    </div>
                  </div>
                )}
              </div>
            )}

            {user.subscription_status === 'inactive' && (
              <div className="bg-gradient-to-r from-orange-500/10 to-pink-600/10 border border-orange-500/20 p-6 rounded-xl">
                <div className="flex items-center space-x-3 mb-4">
                  <Star className="w-6 h-6 text-orange-500" />
                  <h3 className="text-lg font-semibold text-white">Escolha seu plano</h3>
                </div>
                <p className="text-slate-300 mb-4">
                  Você ainda não tem uma assinatura ativa. Escolha um de nossos planos e comece a assistir agora!
                </p>
                <button
                  onClick={() => setActiveTab('subscription')}
                  className="bg-gradient-to-r from-orange-500 to-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-pink-700 transition-all duration-200"
                >
                  Ver Planos
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'subscription' && <SubscriptionManager />}
        {activeTab === 'payments' && <PaymentManagement />}
        {activeTab === 'account' && <AccountSettings />}
        {activeTab === 'tutorials' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
              <h2 className="text-xl font-semibold text-white mb-6">Tutoriais de Instalações</h2>
              
              {user.subscription_status === 'inactive' ? (
                <div className="text-center py-8">
                  <Monitor className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-400 mb-4">Você precisa de uma assinatura ativa para acessar os tutoriais</p>
                  <button
                    onClick={() => setActiveTab('subscription')}
                    className="bg-gradient-to-r from-orange-500 to-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-pink-700 transition-all duration-200"
                  >
                    Ver Planos Disponíveis
                  </button>
                </div>
              ) : (
                <div>
                  {/* Seleção de Plano */}
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-white mb-4">Escolha o tipo de tutorial:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Plano IPTV */}
                      {(user.subscription_plan === 'channels' || user.subscription_plan === 'combo') && (
                        <button
                          onClick={() => setSelectedTutorialPlan('iptv')}
                          className={`p-6 rounded-xl border-2 transition-all ${
                            selectedTutorialPlan === 'iptv'
                              ? 'border-blue-500 bg-blue-500/10'
                              : 'border-slate-600 bg-slate-700/30 hover:border-slate-500'
                          }`}
                        >
                          <div className="text-center">
                            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                              <Monitor className="w-6 h-6 text-blue-400" />
                            </div>
                            <h4 className="text-white font-semibold mb-2">Plano IPTV</h4>
                            <p className="text-slate-400 text-sm">Tutoriais para canais de televisão</p>
                          </div>
                        </button>
                      )}

                      {/* Plano Filmes, Séries */}
                      {(user.subscription_plan === 'movies_series_anime' || user.subscription_plan === 'combo') && (
                        <button
                          onClick={() => setSelectedTutorialPlan('filmes_series')}
                          className={`p-6 rounded-xl border-2 transition-all ${
                            selectedTutorialPlan === 'filmes_series'
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-slate-600 bg-slate-700/30 hover:border-slate-500'
                          }`}
                        >
                          <div className="text-center">
                            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                              <PlayCircle className="w-6 h-6 text-purple-400" />
                            </div>
                            <h4 className="text-white font-semibold mb-2">Plano Filmes, Séries</h4>
                            <p className="text-slate-400 text-sm">Tutoriais para aplicativo Emby</p>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Tutoriais por Dispositivo */}
                  {selectedTutorialPlan && (
                    <div className="bg-slate-700/30 p-6 rounded-xl">
                      <h4 className="text-white font-semibold mb-4">
                        Tutoriais - {selectedTutorialPlan === 'iptv' ? 'IPTV Canais' : 'Filmes e Séries'}
                      </h4>
                      
                      {loadingTutorials ? (
                        <div className="text-center py-8">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                          <p className="text-slate-400 mt-2">Carregando tutoriais...</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {[
                            { key: 'mobile_android', name: 'Mobile Android', icon: '📱' },
                            { key: 'mobile_apple', name: 'Mobile Apple', icon: '🍎' },
                            { key: 'tv_samsung', name: 'TV Samsung', icon: '📺' },
                            { key: 'tv_lg', name: 'TV LG', icon: '📺' },
                            { key: 'tv_android_tv', name: 'TV Android TV', icon: '📺' },
                            { key: 'tv_box', name: 'TV BOX', icon: '📦' },
                            { key: 'apple_tv', name: 'Apple TV', icon: '🍎📺' },
                            { key: 'fire_stick_amazon', name: 'Fire Stick Amazon', icon: '🔥' },
                            { key: 'xiaomi_mi_stick', name: 'Xiaomi Mi Stick', icon: '📱📺' },
                            { key: 'roku_tv', name: 'Roku TV', icon: '📺' },
                            { key: 'pc', name: 'PC', icon: '💻' },
                            ...(selectedTutorialPlan === 'filmes_series' ? [{ key: 'game_xbox', name: 'Game XBOX', icon: '🎮' }] : [])
                          ].map(device => {
                            const tutorial = availableTutorials[selectedTutorialPlan]?.[device.key];
                            
                            return (
                              <div key={device.key} className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                                <div className="text-center">
                                  <div className="text-2xl mb-2">{device.icon}</div>
                                  <h5 className="text-white font-medium mb-3">{device.name}</h5>
                                  
                                  {tutorial ? (
                                    <button
                                      onClick={() => downloadTutorial(tutorial.id, tutorial.filename)}
                                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg font-medium hover:from-green-600 hover:to-emerald-700 transition-all duration-200 flex items-center justify-center space-x-2"
                                    >
                                      <span>📥</span>
                                      <span>Baixar PDF</span>
                                    </button>
                                  ) : (
                                    <div className="w-full bg-slate-700 text-slate-400 px-4 py-2 rounded-lg font-medium cursor-not-allowed">
                                      Tutorial não disponível
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!selectedTutorialPlan && (
                    <div className="text-center py-8">
                      <Monitor className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                      <p className="text-slate-400">Selecione um plano acima para ver os tutoriais disponíveis</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SubscriptionManager = () => {
  const { user, fetchCurrentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [addScreensLoading, setAddScreensLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [extraScreens, setExtraScreens] = useState(0);
  const [screensToAdd, setScreensToAdd] = useState(1);
  // Controle de telas extras por plano
  const [planExtraScreens, setPlanExtraScreens] = useState({
    channels: 0,
    movies_series_anime: 0,
    combo: 0
  });
  // Payment method is always card - no selection needed

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getPlanText = (plan) => {
    switch (plan) {
      case 'channels': return 'Plano Canais';
      case 'movies_series_anime': return 'Filmes, Séries e Animes';
      case 'combo': return 'Combo (Canais + Filmes/Séries)';
      default: return 'Nenhum plano';
    }
  };

  const plans = [
    {
      id: 'channels',
      name: 'Plano Canais',
      price: 25.00,
      extraScreenPrice: 15.00,
      features: [
        'Acesso a todos os canais',
        'Transmissão ao vivo',
        'Qualidade HD',
        '1 tela incluída'
      ],
      icon: <PlayCircle className="w-8 h-8" />,
      gradient: 'from-blue-500 to-cyan-600'
    },
    {
      id: 'movies_series_anime',
      name: 'Filmes, Séries e Animes',
      price: 25.00,
      extraScreenPrice: 15.00,
      features: [
        'Filmes em HD/4K',
        'Séries completas',
        'Animes legendados e dublados',
        '1 tela incluída'
      ],
      icon: <Monitor className="w-8 h-8" />,
      gradient: 'from-purple-500 to-pink-600'
    },
    {
      id: 'combo',
      name: 'Combo Completo',
      price: 40.00,
      originalPrice: 50.00,
      discount: '20% OFF',
      extraScreenPrice: 20.00,
      features: [
        'Todos os canais',
        'Filmes, séries e animes',
        'Qualidade HD/4K',
        '1 tela incluída',
        'Melhor custo-benefício'
      ],
      icon: <Crown className="w-8 h-8" />,
      gradient: 'from-orange-500 to-red-600',
      popular: true
    }
  ];

  const handleAddScreens = async () => {
    if (addScreensLoading) return;
    
    // Validação para usuários com combo
    if (user?.subscription_plan === 'combo' && !selectedPlan) {
      toast.error('Selecione um plano para adicionar telas');
      return;
    }
    
    setAddScreensLoading(true);
    try {
      const requestData = {
        extra_screens_to_add: screensToAdd
      };
      
      // Para usuários com combo, incluir o plano específico
      if (user?.subscription_plan === 'combo') {
        requestData.target_plan = selectedPlan;
      }
      
      const response = await axios.post(`${API}/subscription/add-screens`, requestData, {
        headers: {
          'origin': window.location.origin
        }
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error adding screens:', error);
      toast.error(error.response?.data?.detail || 'Erro ao adicionar telas');
    } finally {
      setAddScreensLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      // Para novos usuários, usar as telas extras específicas do plano selecionado
      const extraScreensForPlan = user?.subscription_status !== 'active' 
        ? planExtraScreens[selectedPlan] || 0 
        : extraScreens;

      const response = await axios.post(`${API}/subscription/checkout`, {
        plan_type: selectedPlan,
        extra_screens: extraScreensForPlan,
        payment_method: "card"
      }, {
        headers: {
          'origin': window.location.origin
        }
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    setCancelLoading(true);
    try {
      await axios.post(`${API}/subscription/cancel`);
      toast.success('Assinatura cancelada com sucesso!');
      await fetchCurrentUser();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cancelar assinatura');
    } finally {
      setCancelLoading(false);
    }
  };

  const getAvailablePlans = () => {
    if (user?.subscription_status !== 'active') {
      return plans; // Show all plans for non-active users
    }

    const currentPlan = user.subscription_plan;
    
    // Filter plans based on current subscription
    const availablePlans = plans.filter(plan => {
      if (currentPlan === 'channels' && plan.id === 'movies_series_anime') {
        return true; // Can add Movies/Series to Channels
      }
      if (currentPlan === 'movies_series_anime' && plan.id === 'channels') {
        return true; // Can add Channels to Movies/Series
      }
      if ((currentPlan === 'channels' || currentPlan === 'movies_series_anime') && plan.id === 'combo') {
        return true; // Can upgrade to combo
      }
      return false; // Don't show same plan or invalid combinations
    });

    return availablePlans;
  };

  // Set default selected plan when available plans change
  React.useEffect(() => {
    const available = getAvailablePlans();
    if (available.length > 0 && !selectedPlan) {
      setSelectedPlan(available[0].id);
    }
  }, [user?.subscription_status, user?.subscription_plan]);

  const calculateTotal = () => {
    const availablePlans = getAvailablePlans();
    const plan = availablePlans.find(p => p.id === selectedPlan);
    const planPrice = plan?.price || 0;
    
    // Para usuários ativos (com plano)
    if (user?.subscription_status === 'active') {
      const currentPlan = user.subscription_plan;
      if (selectedPlan === 'combo' && (currentPlan === 'channels' || currentPlan === 'movies_series_anime')) {
        // Nova regra: upgrade para combo com 20% desconto = R$ 15,00
        const extraScreensPrice = extraScreens * 15; // R$ 15 por tela para usuários ativos
        return 15 + extraScreensPrice;
      }
      // Adicionando telas ao plano atual: R$ 10 para combo, R$ 15 para individuais
      const screenPrice = user.subscription_plan === 'combo' ? 10 : 15;
      const extraScreensPrice = extraScreens * screenPrice;
      return planPrice + extraScreensPrice;
    }
    
    // Novos usuários: usar preços corretos por plano
    if (selectedPlan && planExtraScreens[selectedPlan] > 0) {
      // Planos individuais: R$ 15,00 por tela | Combo: R$ 20,00 por tela
      const extraScreenPrice = selectedPlan === 'combo' ? 20 : 15;
      const extraScreensPrice = planExtraScreens[selectedPlan] * extraScreenPrice;
      return planPrice + extraScreensPrice;
    }
    
    return planPrice;
  };

  return (
    <div className="space-y-6">
      {/* Current Subscription Info */}
      {user?.subscription_status === 'active' && (
        <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700 mb-6">
          <h3 className="text-xl font-semibold text-white mb-4">Assinatura Atual</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-1">Plano</h4>
              <p className="text-white font-medium">{getPlanText(user.subscription_plan)}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-1">Telas Disponíveis</h4>
              {user.subscription_plan === 'combo' ? (
                <div className="space-y-1">
                  <p className="text-white font-medium">Total: {1 + (user.extra_screens || 0)}</p>
                  <div className="text-xs text-slate-400">
                    <p>• Padrão: 1 tela</p>
                    <p>• Extra Canais: {user.extra_screens_channels || 0}</p>
                    <p>• Extra Filmes/Séries: {user.extra_screens_movies || 0}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-white font-medium">Total: {1 + (user.extra_screens || 0)}</p>
                  <div className="text-xs text-slate-400">
                    <p>• Padrão: 1 tela</p>
                    {user.extra_screens > 0 && (
                      <p>• Extra: {user.extra_screens} tela{user.extra_screens > 1 ? 's' : ''}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-1">Próxima Cobrança</h4>
              <p className="text-white font-medium">{formatDate(user.next_billing_date)}</p>
            </div>
          </div>

          {/* Ações para usuários com plano ativo */}
          <div className="border-t border-slate-600 pt-6 mb-6">
            <h4 className="text-lg font-semibold text-white mb-4">Gerenciar Assinatura</h4>
            
            <div className="space-y-4">
              {/* Adicionar telas por plano específico - APENAS PARA COMBO */}
              {user.subscription_plan === 'combo' ? (
                <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-600">
                  <h5 className="text-white font-medium mb-2">Adicionar Telas por Plano</h5>
                  <p className="text-slate-400 text-sm mb-4">
                    Escolha para qual plano deseja adicionar telas extras. 
                    <span className="font-medium text-white"> R$ 10,00 por tela por plano específico.</span>
                  </p>
                  
                  {/* Seleção de plano */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <button
                      onClick={() => setSelectedPlan('channels')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        selectedPlan === 'channels'
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-slate-600 bg-slate-700/30 hover:border-slate-500'
                      }`}
                    >
                      <div className="text-left">
                        <h6 className="text-white font-medium mb-1">Plano Canais</h6>
                        <p className="text-slate-400 text-sm">Adicionar telas para canais</p>
                        <p className="text-xs text-slate-500 mt-1">Atual: {user.extra_screens_channels || 0} telas</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setSelectedPlan('movies_series_anime')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        selectedPlan === 'movies_series_anime'
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-slate-600 bg-slate-700/30 hover:border-slate-500'
                      }`}
                    >
                      <div className="text-left">
                        <h6 className="text-white font-medium mb-1">Filmes, Séries e Animes</h6>
                        <p className="text-slate-400 text-sm">Adicionar telas para filmes/séries</p>
                        <p className="text-xs text-slate-500 mt-1">Atual: {user.extra_screens_movies || 0} telas</p>
                      </div>
                    </button>
                  </div>

                  {selectedPlan && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4 mb-4">
                        <button
                          onClick={() => setScreensToAdd(Math.max(1, screensToAdd - 1))}
                          className="w-10 h-10 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center justify-center transition-colors"
                          disabled={screensToAdd === 1}
                        >
                          -
                        </button>
                        <span className="text-white font-medium min-w-[120px] text-center">
                          {screensToAdd} {screensToAdd === 1 ? 'tela' : 'telas'}
                        </span>
                        <button
                          onClick={() => setScreensToAdd(screensToAdd + 1)}
                          className="w-10 h-10 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center justify-center transition-colors"
                        >
                          +
                        </button>
                        <span className="text-slate-400">
                          Total: R$ {(screensToAdd * (user.subscription_plan === 'combo' ? 10 : 15)).toFixed(2)}
                        </span>
                      </div>
                      
                      <div className="bg-blue-900/20 border border-blue-500/30 p-3 rounded-lg">
                        <p className="text-blue-400 text-sm">
                          <strong>Plano selecionado:</strong> {getPlanText(selectedPlan)} • {screensToAdd} tela{screensToAdd > 1 ? 's' : ''} por R$ {(screensToAdd * (user.subscription_plan === 'combo' ? 10 : 15)).toFixed(2)}
                        </p>
                      </div>

                      <button
                        onClick={handleAddScreens}
                        disabled={addScreensLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {addScreensLoading ? 'Processando...' : 'Adicionar Telas'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* Opções para usuários de plano único */
                <div className="space-y-4">
                  {/* Adicionar telas ao plano atual */}
                  <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-600">
                    <h5 className="text-white font-medium mb-2">Adicionar Telas ao Seu Plano</h5>
                    <p className="text-slate-400 text-sm mb-4">
                      Adicione mais telas ao seu plano {getPlanText(user.subscription_plan).toLowerCase()}. 
                      <span className="font-medium text-white"> R$ 15,00 por tela adicional.</span>
                    </p>
                    
                    <div className="flex items-center space-x-4 mb-4">
                      <button
                        onClick={() => setScreensToAdd(Math.max(1, screensToAdd - 1))}
                        className="w-10 h-10 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center justify-center transition-colors"
                        disabled={screensToAdd === 1}
                      >
                        -
                      </button>
                      <span className="text-white font-medium min-w-[80px] text-center">
                        {screensToAdd} {screensToAdd === 1 ? 'tela' : 'telas'}
                      </span>
                      <button
                        onClick={() => setScreensToAdd(screensToAdd + 1)}
                        className="w-10 h-10 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center justify-center transition-colors"
                      >
                        +
                      </button>
                      <span className="text-slate-400">
                        Total: R$ {(screensToAdd * 15).toFixed(2)}
                      </span>
                    </div>

                    <button
                      onClick={handleAddScreens}
                      disabled={addScreensLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addScreensLoading ? 'Processando...' : 'Adicionar Telas'}
                    </button>
                  </div>

                  {/* Upgrade para combo */}
                  <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 p-4 rounded-lg border border-orange-500/30">
                    <h5 className="text-white font-medium mb-2">Upgrade para Combo</h5>
                    <p className="text-slate-300 text-sm mb-4">
                      Adicione o plano complementar com <span className="font-medium text-green-400">20% de desconto</span> por apenas 
                      <span className="font-medium text-orange-400"> R$ 15,00 adicional</span>. 
                      Total: R$ 40,00/mês (economia de R$ 10,00).
                    </p>
                    <p className="text-slate-400 text-xs mb-4">
                      💡 <strong>Após o upgrade:</strong> Você poderá adicionar telas específicas para cada plano por apenas R$ 10,00 cada.
                    </p>
                    
                    <button
                      onClick={() => {
                        setSelectedPlan('combo');
                        setExtraScreens(0); // Reset telas extras para o upgrade
                      }}
                      className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                      Upgrade para Combo
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleCancelSubscription}
            disabled={cancelLoading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelLoading ? 'Cancelando...' : 'Cancelar Assinatura'}
          </button>
        </div>
      )}

      {/* Plan selection logic - Apenas para usuários sem plano */}
      {user?.subscription_status !== 'active' && (
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">Escolha seu Plano</h2>
          <p className="text-slate-400">
            Selecione o plano que melhor se adequa às suas necessidades
          </p>
        </div>
      )}

      {getAvailablePlans().length > 0 && user?.subscription_status !== 'active' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {getAvailablePlans().map((plan) => {
          // Adjust display for upgrades
          let displayPrice = plan.price;
          let displayNote = null;
          
          if (user?.subscription_status === 'active') {
            const currentPlan = user.subscription_plan;
            if (plan.id === 'combo' && (currentPlan === 'channels' || currentPlan === 'movies_series_anime')) {
              displayPrice = 25; // Nova regra: sem desconto
              displayNote = `Upgrade: R$ 25,00 adicional (sem desconto)`;
            } else if (plan.id !== 'combo') {
              displayNote = `Adicional ao seu plano atual`;
            }
          }
          
          return (
          <div
            key={plan.id}
            className={`relative bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border transition-all duration-200 cursor-pointer ${
              selectedPlan === plan.id
                ? 'border-orange-500 ring-2 ring-orange-500/20'
                : 'border-slate-700 hover:border-slate-600'
            }`}
            onClick={() => setSelectedPlan(plan.id)}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <div className="bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-medium px-3 py-1 rounded-full">
                  Mais Popular
                </div>
              </div>
            )}

            <div className={`w-12 h-12 bg-gradient-to-r ${plan.gradient} rounded-lg flex items-center justify-center text-white mb-4`}>
              {plan.icon}
            </div>

            <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
            
            <div className="mb-4">
              <div className="flex items-baseline space-x-2">
                <span className="text-3xl font-bold text-white">R$ {displayPrice.toFixed(2)}</span>
                <span className="text-slate-400">/mês</span>
                {plan.originalPrice && displayPrice === plan.price && (
                  <span className="text-sm text-slate-500 line-through">R$ {plan.originalPrice.toFixed(2)}</span>
                )}
              </div>
              {plan.discount && displayPrice === plan.price && (
                <div className="text-green-500 text-sm font-medium mt-1">{plan.discount}</div>
              )}
              {displayNote && (
                <div className="text-orange-400 text-sm font-medium mt-1">{displayNote}</div>
              )}
            </div>

            <ul className="space-y-3 mb-6">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-center space-x-3">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Seleção de telas extras dentro do card */}
            {user?.subscription_status !== 'active' && (
              <div className="border-t border-slate-700 pt-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Telas extras</span>
                  <span className="text-xs text-slate-500">+R$ {plan.id === 'combo' ? '20' : '15'}/tela</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlanExtraScreens(prev => ({
                        ...prev,
                        [plan.id]: Math.max(0, prev[plan.id] - 1)
                      }));
                    }}
                    className="w-8 h-8 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center justify-center transition-colors text-sm"
                    disabled={planExtraScreens[plan.id] === 0}
                  >
                    -
                  </button>
                  
                  <span className="text-white font-medium min-w-[60px] text-center text-sm">
                    {planExtraScreens[plan.id]} telas
                  </span>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlanExtraScreens(prev => ({
                        ...prev,
                        [plan.id]: prev[plan.id] + 1
                      }));
                    }}
                    className="w-8 h-8 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center justify-center transition-colors text-sm"
                  >
                    +
                  </button>
                </div>
                
                {planExtraScreens[plan.id] > 0 && (
                  <div className="text-center mt-2">
                    <span className="text-xs text-orange-400">
                      +R$ {(planExtraScreens[plan.id] * (plan.id === 'combo' ? 20 : 15)).toFixed(2)} em telas extras
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className={`w-4 h-4 rounded-full border-2 ${
              selectedPlan === plan.id ? 'border-orange-500 bg-orange-500' : 'border-slate-600'
            } absolute top-4 right-4`}>
              {selectedPlan === plan.id && (
                <Check className="w-3 h-3 text-white absolute top-0.5 left-0.5" />
              )}
            </div>
          </div>
        );
        })}
        </div>
      )}

      {/* Seção "Telas Adicionais" removida - integrada nos cards de plano */}

      {/* Payment Method e Resumo - Apenas para usuários sem plano ativo */}
      {user?.subscription_status !== 'active' && selectedPlan && (
        <>
          {/* Payment Method - Card Only */}
          <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Método de Pagamento</h3>
            <div className="p-4 rounded-lg border border-orange-500 bg-orange-500/10">
              <div className="flex items-center space-x-3">
                <CreditCard className="w-5 h-5 text-orange-500" />
                <div>
                  <h4 className="text-white font-medium">Cartão de Crédito</h4>
                  <p className="text-slate-400 text-sm">Pagamento seguro e instantâneo</p>
                </div>
              </div>
            </div>
          </div>

          {/* Total and Subscribe */}
          <div className="bg-gradient-to-r from-orange-500/10 to-pink-600/10 border border-orange-500/20 p-6 rounded-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">Total Mensal</h3>
              <div className="text-3xl font-bold text-white">
                R$ {calculateTotal().toFixed(2)}
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-slate-300 mb-6">
              {(() => {
                const plan = plans.find(p => p.id === selectedPlan);
                
                return (
                  <>
                    <div className="flex justify-between">
                      <span>{plan?.name}</span>
                      <span>R$ {plan?.price.toFixed(2)}</span>
                    </div>
                    
                    {/* Para novos usuários com telas extras no plano */}
                    {planExtraScreens[selectedPlan] > 0 && (
                      <div className="flex justify-between">
                        <span>{planExtraScreens[selectedPlan]} tela{planExtraScreens[selectedPlan] > 1 ? 's' : ''} extra{planExtraScreens[selectedPlan] > 1 ? 's' : ''}</span>
                        <span>R$ {(planExtraScreens[selectedPlan] * (selectedPlan === 'combo' ? 20 : 15)).toFixed(2)}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processando...' : 'Assinar Agora'}
            </button>
          </div>
        </>
      )}

      {/* Resumo para upgrade - Apenas para usuários ativos */}
      {user?.subscription_status === 'active' && selectedPlan === 'combo' && (
        <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 p-6 rounded-xl">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-white">Resumo do Upgrade</h3>
            <div className="text-3xl font-bold text-white">
              R$ 15,00
            </div>
          </div>
          
          <div className="space-y-2 text-sm text-slate-300 mb-6">
            <div className="flex justify-between">
              <span>Upgrade para Combo Completo</span>
              <span>R$ 15,00</span>
            </div>
            <div className="text-xs text-green-400 italic">
              Upgrade com 20% desconto (total mensal: R$ 40,00)
            </div>
          </div>

          <button
            onClick={() => {
              setSelectedPlan('combo');
              setExtraScreens(0);
              handleSubscribe();
            }}
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-500 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-green-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processando...' : 'Fazer Upgrade'}
          </button>
        </div>
      )}
      )}
    </div>
  );
};

const PaymentManagement = () => {
  const { user, fetchCurrentUser } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPaymentData();
  }, []);

  const fetchPaymentData = async () => {
    setLoading(true);
    try {
      // Fetch payment methods
      const methodsResponse = await axios.get(`${API}/payments/methods`);
      let methods = methodsResponse.data.payment_methods || [];
      
      // Filter out any demo cards that might have been added
      methods = methods.filter(method => 
        !method.id?.toString().startsWith('pm_') || 
        (method.card && method.card.last4 !== '1234')
      );
      
      setPaymentMethods(methods);
      
      // Fetch transaction history
      const transactionsResponse = await axios.get(`${API}/payments/transactions`);
      setTransactions(transactionsResponse.data.transactions || []);
    } catch (error) {
      console.error('Error fetching payment data:', error);
      toast.error('Erro ao carregar dados de pagamento');
    } finally {
      setLoading(false);
    }
  };

  // Funções de gerenciamento de cartão removidas - foco apenas no histórico

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR') + ' às ' + date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCardBrandIcon = (brand) => {
    const brandColors = {
      visa: 'text-blue-600',
      mastercard: 'text-red-600',
      amex: 'text-green-600',
      default: 'text-gray-600'
    };
    return brandColors[brand] || brandColors.default;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Settings className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Transaction History */}
      <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
        <h2 className="text-xl font-semibold text-white mb-6">Histórico de Pagamentos</h2>

        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Nenhuma transação</h3>
            <p className="text-slate-400">Seus pagamentos aparecerão aqui</p>
          </div>
        ) : (
          <div className="space-y-4">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="p-4 bg-slate-700/30 rounded-lg border border-slate-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      transaction.status === 'paid' ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}>
                      {transaction.status === 'paid' ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <X className="w-5 h-5 text-red-500" />
                      )}
                    </div>
                    <div>
                      <div className="text-white font-medium">
                        R$ {transaction.amount.toFixed(2)}
                      </div>
                      <div className="text-slate-400 text-sm">
                        Cartão de Crédito • {formatDate(transaction.created_at)}
                      </div>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    transaction.status === 'paid' 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-red-500/20 text-red-500'
                  }`}>
                    {transaction.status === 'paid' ? 'Pago' : 'Pendente'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AccountSettings = () => {
  const { user, logout, fetchCurrentUser } = useAuth();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Nova senha e confirmação não coincidem');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      toast.success('Senha alterada com sucesso! A senha foi sincronizada com o servidor Emby.');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('⚠️ ATENÇÃO: Esta ação irá excluir permanentemente sua conta e todos os seus dados. Esta ação NÃO PODE ser desfeita. Tem certeza que deseja continuar?')) {
      return;
    }
    
    const confirmation = prompt('Digite "EXCLUIR" para confirmar a exclusão da conta:');
    if (confirmation !== 'EXCLUIR') {
      return;
    }
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/auth/delete-account`);
      toast.success('Conta excluída com sucesso. Você será redirecionado.');
      setTimeout(() => {
        logout();
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir conta');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Digite seu email');
      return;
    }

    setForgotLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotEmail
      });

      if (response.data.success) {
        setTempPassword('enviado'); // Flag para mostrar confirmação
        toast.success('Senha temporária enviada por email!');
      }
    } catch (error) {
      console.error('Error in forgot password:', error);
      toast.error(error.response?.data?.detail || 'Erro ao recuperar senha');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setTempPassword('');
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
        <h2 className="text-xl font-semibold text-white mb-4">Informações da Conta</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Nome</h3>
            <p className="text-white">{user?.name}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Email</h3>
            <p className="text-white">{user?.email}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Status da Conta</h3>
            <p className={`font-medium ${user?.is_active ? 'text-green-500' : 'text-red-500'}`}>
              {user?.is_active ? 'Ativa' : 'Inativa'}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Data de Criação</h3>
            <p className="text-white">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
        <h2 className="text-xl font-semibold text-white mb-4">Alterar Senha</h2>
        <p className="text-slate-400 mb-6">
          Altere sua senha do AnhomeWeb. A nova senha será automaticamente sincronizada com o servidor Emby.
        </p>
        
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Senha Atual</label>
            <input
              type="password"
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Nova Senha</label>
            <input
              type="password"
              required
              minLength="6"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Confirmar Nova Senha</label>
            <input
              type="password"
              required
              minLength="6"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
            />
          </div>
          
          <button
            type="submit"
            disabled={passwordLoading}
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {passwordLoading ? (
              <>
                <Settings className="w-4 h-4 mr-2 animate-spin" />
                Alterando...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" />
                Alterar Senha
              </>
            )}
          </button>
        </form>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-900/20 border border-red-500/30 p-6 rounded-xl">
        <h2 className="text-xl font-semibold text-red-400 mb-4 flex items-center">
          <Trash2 className="w-5 h-5 mr-2" />
          Zona de Perigo
        </h2>
        <p className="text-slate-300 mb-4">
          A exclusão da conta é permanente e não pode ser desfeita. Todos os seus dados serão removidos permanentemente, incluindo:
        </p>
        <ul className="list-disc list-inside text-slate-300 mb-6 space-y-1">
          <li>Sua conta no AnhomeWeb e no servidor Emby</li>
          <li>Histórico de pagamentos e assinaturas</li>
          <li>Preferências e configurações</li>
          <li>Todos os dados pessoais</li>
        </ul>
        <button
          onClick={handleDeleteAccount}
          disabled={deleteLoading}
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {deleteLoading ? (
            <>
              <Settings className="w-4 h-4 mr-2 animate-spin" />
              Excluindo...
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir Conta Permanentemente
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Estados para usuários administrativos
  const [currentUsername, setCurrentUsername] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdminUsers, setLoadingAdminUsers] = useState(false);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [showAdminPasswordModal, setShowAdminPasswordModal] = useState(false);
  const [showOwnPasswordModal, setShowOwnPasswordModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newAdminData, setNewAdminData] = useState({ username: '', email: '', password: '' });
  const [adminPasswordData, setAdminPasswordData] = useState({ current_password: '', new_password: '' });
  const [adminPasswordLoading, setAdminPasswordLoading] = useState(false);
  
  // Estados para tutoriais
  const [adminActiveTab, setAdminActiveTab] = useState('dashboard');
  const [tutorials, setTutorials] = useState({ iptv: {}, filmes_series: {} });
  const [loadingTutorials, setLoadingTutorials] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedUploadPlan, setSelectedUploadPlan] = useState('');
  const [selectedUploadDevice, setSelectedUploadDevice] = useState('');
  
  // Estados para Stripe
  const [stripeProducts, setStripeProducts] = useState([]);
  const [loadingStripeProducts, setLoadingStripeProducts] = useState(false);
  const [stripeMapping, setStripeMapping] = useState({});
  const [savingMapping, setSavingMapping] = useState(false);
  
  // Estados para Configurações Informativas
  const [informativeSettings, setInformativeSettings] = useState({
    telegram_group_link: '',
    informative_filmes_text: '',
    informative_tv_text: ''
  });
  const [loadingInformativeSettings, setLoadingInformativeSettings] = useState(false);
  const [savingInformativeSettings, setSavingInformativeSettings] = useState(false);
  
  // Estados para Configurações de Email
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_use_tls: true,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    bcc_email: '',
    template_welcome: '',
    template_payment_success: '',
    template_payment_failed: '',
    template_cancellation: '',
    template_password_reset: ''
  });
  const [loadingEmailSettings, setLoadingEmailSettings] = useState(false);
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);
  const [activeEmailTab, setActiveEmailTab] = useState('smtp');
  
  // Email Templates State
  const [emailTemplates, setEmailTemplates] = useState({
    welcome: '',
    payment_success: '',
    payment_failed: '',
    cancellation: '',
    password_reset: ''
  });
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState('');
  const [restoringTemplate, setRestoringTemplate] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // SMTP Test State
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  
  // Custom Email Send State
  const [customEmailData, setCustomEmailData] = useState({
    recipient_email: '',
    subject: '',
    html_content: ''
  });
  const [usersEmails, setUsersEmails] = useState([]);
  const [loadingUsersEmails, setLoadingUsersEmails] = useState(false);
  const [sendingCustomEmail, setSendingCustomEmail] = useState(false);
  const [customEmailMode, setCustomEmailMode] = useState('select'); // 'select' ou 'custom'
  
  useEffect(() => {
    if (adminToken) {
      fetchDashboard();
      fetchUsers();
      fetchCurrentAdminInfo();
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken && adminActiveTab === 'informative') {
      fetchInformativeSettings();
    }
  }, [adminToken, adminActiveTab]);

  useEffect(() => {
    if (adminToken && adminActiveTab === 'email') {
      fetchEmailSettings();
      fetchEmailTemplates();
      fetchUsersEmails();
    }
  }, [adminToken, adminActiveTab]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      const { access_token, username } = response.data;
      
      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('admin_username', username || 'svc.admin');
      setAdminToken(access_token);
      setCurrentUsername(username || 'svc.admin');
      toast.success('Login administrativo realizado com sucesso!');
    } catch (error) {
      toast.error('Credenciais administrativas inválidas');
    } finally {
      setLoginLoading(false);
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        setAdminToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUsersList(response.data.users);
      setFilteredUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar lista de usuários');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term) {
      setFilteredUsers(usersList);
    } else {
      const filtered = usersList.filter(user =>
        user.name.toLowerCase().includes(term.toLowerCase()) ||
        user.email.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.put(`${API}/admin/user/${selectedUser.id}/password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      toast.success(`Senha alterada para ${selectedUser.name}`);
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
      fetchUsers(); // Refresh users list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const fetchCurrentAdminInfo = () => {
    const username = localStorage.getItem('admin_username');
    if (username) {
      setCurrentUsername(username);
      if (username === 'svc.admin') {
        fetchAdminUsers();
      }
    }
  };

  const fetchAdminUsers = async () => {
    if (currentUsername !== 'svc.admin') return;
    
    setLoadingAdminUsers(true);
    try {
      const response = await axios.get(`${API}/admin/admin-users`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAdminUsers(response.data.admin_users);
    } catch (error) {
      toast.error('Erro ao carregar usuários administrativos');
    } finally {
      setLoadingAdminUsers(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdminData.username || !newAdminData.email || !newAdminData.password) {
      toast.error('Todos os campos são obrigatórios');
      return;
    }

    setAdminPasswordLoading(true);
    try {
      await axios.post(`${API}/admin/create-admin-user`, newAdminData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Usuário administrativo ${newAdminData.username} criado com sucesso`);
      setShowCreateAdminModal(false);
      setNewAdminData({ username: '', email: '', password: '' });
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário administrativo');
    } finally {
      setAdminPasswordLoading(false);
    }
  };

  const handleAdminPasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setAdminPasswordLoading(true);
    try {
      await axios.put(`${API}/admin/admin-user/${selectedAdmin.id}/password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      toast.success(`Senha alterada para ${selectedAdmin.username}`);
      setShowAdminPasswordModal(false);
      setNewPassword('');
      setSelectedAdmin(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setAdminPasswordLoading(false);
    }
  };

  const handleOwnPasswordChange = async () => {
    if (!adminPasswordData.current_password || !adminPasswordData.new_password) {
      toast.error('Todos os campos são obrigatórios');
      return;
    }

    if (adminPasswordData.new_password.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setAdminPasswordLoading(true);
    try {
      await axios.put(`${API}/admin/change-password`, adminPasswordData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Sua senha foi alterada com sucesso');
      setShowOwnPasswordModal(false);
      setAdminPasswordData({ current_password: '', new_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setAdminPasswordLoading(false);
    }
  };

  const handleAdminStatusChange = async (adminId, isActive) => {
    try {
      await axios.put(`${API}/admin/admin-user/${adminId}/status`, 
        { is_active: isActive },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      const action = isActive ? 'ativado' : 'desativado';
      toast.success(`Usuário ${action} com sucesso`);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const handleDeleteAdmin = async (adminId, username) => {
    if (!window.confirm(`Excluir permanentemente o usuário administrativo ${username}?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/admin-user/${adminId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Usuário ${username} excluído com sucesso`);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  // Funções para Configurações de Email
  const fetchEmailSettings = async () => {
    setLoadingEmailSettings(true);
    try {
      const response = await axios.get(`${API}/admin/email-settings`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setEmailSettings(response.data.settings);
    } catch (error) {
      console.error('Erro ao carregar configurações de email:', error);
      toast.error('Erro ao carregar configurações de email');
    } finally {
      setLoadingEmailSettings(false);
    }
  };

  const saveEmailSettings = async () => {
    setSavingEmailSettings(true);
    setSmtpTestResult(null); // Limpar resultado de teste anterior
    try {
      await axios.post(
        `${API}/admin/email-settings`,
        emailSettings,
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      toast.success('Configurações de email salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações de email:', error);
      toast.error('Erro ao salvar configurações de email');
    } finally {
      setSavingEmailSettings(false);
    }
  };

  const testSmtpConfiguration = async () => {
    setTestingSmtp(true);
    setSmtpTestResult(null);
    
    try {
      // Solicitar email de teste ao usuário
      const testEmail = prompt('Digite o email para receber o teste:', emailSettings.smtp_username || 'notificacao@anhomeweb.com');
      
      if (!testEmail) {
        setTestingSmtp(false);
        return;
      }
      
      // Validar se a senha não está mascarada
      if (emailSettings.smtp_password === '***********') {
        toast.error('Por favor, digite a senha SMTP real antes de testar');
        setTestingSmtp(false);
        return;
      }
      
      const response = await axios.post(
        `${API}/admin/test-smtp`,
        {
          ...emailSettings,
          test_email: testEmail
        },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      setSmtpTestResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Erro ao testar SMTP:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Erro ao testar configuração SMTP';
      toast.error(errorMessage);
      setSmtpTestResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setTestingSmtp(false);
    }
  };

  // Custom Email Send Functions
  const fetchUsersEmails = async () => {
    setLoadingUsersEmails(true);
    try {
      const response = await axios.get(`${API}/admin/users-emails`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUsersEmails(response.data.emails);
    } catch (error) {
      console.error('Erro ao carregar emails dos usuários:', error);
      toast.error('Erro ao carregar lista de emails');
    } finally {
      setLoadingUsersEmails(false);
    }
  };

  const sendCustomEmail = async () => {
    if (!customEmailData.recipient_email || !customEmailData.subject || !customEmailData.html_content) {
      toast.error('Preencha todos os campos antes de enviar');
      return;
    }
    
    setSendingCustomEmail(true);
    try {
      const response = await axios.post(
        `${API}/admin/send-custom-email`,
        customEmailData,
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Limpar formulário
        setCustomEmailData({
          recipient_email: '',
          subject: '',
          html_content: ''
        });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Erro ao enviar email customizado:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar email');
    } finally {
      setSendingCustomEmail(false);
    }
  };

  const previewCustomEmail = () => {
    if (!customEmailData.html_content) {
      toast.error('Adicione conteúdo ao email antes de visualizar');
      return;
    }
    
    setPreviewTemplate(customEmailData.html_content);
    setShowPreviewModal(true);
  };

  // Email Templates Functions
  const fetchEmailTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await axios.get(`${API}/admin/email-templates`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setEmailTemplates(response.data.templates);
    } catch (error) {
      console.error('Erro ao carregar templates de email:', error);
      toast.error('Erro ao carregar templates de email');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveEmailTemplate = async (templateName) => {
    setSavingTemplate(templateName);
    try {
      await axios.post(
        `${API}/admin/email-templates/save`,
        {
          template_name: templateName,
          template_content: emailTemplates[templateName]
        },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      toast.success(`Template ${templateName} salvo com sucesso!`);
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      toast.error('Erro ao salvar template');
    } finally {
      setSavingTemplate('');
    }
  };

  const restoreEmailTemplate = async (templateName) => {
    if (!window.confirm(`Tem certeza que deseja restaurar o template ${templateName} para o padrão? Esta ação não pode ser desfeita.`)) {
      return;
    }
    
    setRestoringTemplate(templateName);
    try {
      const response = await axios.post(
        `${API}/admin/email-templates/restore/${templateName}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      // Atualizar o template no state com o padrão restaurado
      setEmailTemplates(prev => ({
        ...prev,
        [templateName]: response.data.template
      }));
      
      toast.success(`Template ${templateName} restaurado para o padrão!`);
    } catch (error) {
      console.error('Erro ao restaurar template:', error);
      toast.error('Erro ao restaurar template');
    } finally {
      setRestoringTemplate('');
    }
  };

  const previewEmailTemplate = (templateName) => {
    // Substituir variáveis com dados de exemplo para preview
    let previewHtml = emailTemplates[templateName];
    
    // Dados de exemplo para preview
    const exampleData = {
      user_name: 'João Silva',
      plan_name: 'Combo',
      plan_display: 'Combo',
      'amount:.2f': '40.00',
      temp_password: 'Abc123Xyz',
      reason_html: '<p><strong>Motivo:</strong> Cartão recusado</p>',
      frontend_url: '<a href="https://anhomeweb.com/login">anhomeweb.com</a>'
    };
    
    // Substituir todas as variáveis
    Object.keys(exampleData).forEach(key => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      previewHtml = previewHtml.replace(regex, exampleData[key]);
    });
    
    setPreviewTemplate(previewHtml);
    setShowPreviewModal(true);
  };

  const openCreateAdminModal = () => {
    setNewAdminData({ username: '', email: '', password: '' });
    setShowCreateAdminModal(true);
  };

  const openAdminPasswordModal = (admin) => {
    setSelectedAdmin(admin);
    setNewPassword('');
    setShowAdminPasswordModal(true);
  };

  const openOwnPasswordModal = () => {
    setAdminPasswordData({ current_password: '', new_password: '' });
    setShowOwnPasswordModal(true);
  };

  const manageUser = async (userId, action) => {
    try {
      await axios.post(`${API}/admin/manage-user`, 
        { user_id: userId, action },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      toast.success(`Usuário ${action === 'activate' ? 'ativado' : action === 'deactivate' ? 'desativado' : 'excluído'} com sucesso!`);
      fetchDashboard();
      fetchUsers();
    } catch (error) {
      toast.error(`Erro ao ${action} usuário`);
    }
  };

  const cancelUserSubscription = async (userId, userName) => {
    if (!window.confirm(`Cancelar a assinatura de ${userName}?\n\nO usuário perderá acesso aos serviços mas a conta não será excluída.`)) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/user/${userId}/cancel-subscription`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      toast.success(`Assinatura de ${userName} cancelada com sucesso!`);
      fetchDashboard();
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao cancelar assinatura';
      toast.error(errorMsg);
    }
  };

  const createEmbyAccount = async (userId, userName, userEmail) => {
    if (!window.confirm(`Criar conta Emby para ${userName} (${userEmail})?\n\nUma senha temporária será gerada. O usuário deverá usar "Esqueci a senha" para definir uma nova senha.`)) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/user/${userId}/create-emby-account`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      if (response.data.success) {
        toast.success(`Conta Emby criada com sucesso para ${userName}!`);
        toast.info(`ID Emby: ${response.data.emby_user_id}`, { duration: 5000 });
        toast.warning(`Informe ao usuário para usar "Esqueci a senha" para definir nova senha`, { duration: 7000 });
        
        // Atualizar lista de usuários para mostrar o novo emby_user_id
        fetchUsers();
      } else {
        toast.error(response.data.message || 'Erro ao criar conta Emby');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao criar conta Emby';
      toast.error(errorMsg);
      console.error('Erro ao criar conta Emby:', error);
    }
  };


  // Funções para Configurações Informativas
  const fetchInformativeSettings = async () => {
    setLoadingInformativeSettings(true);
    try {
      const response = await axios.get(`${API}/admin/informative-settings`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setInformativeSettings(response.data.settings);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações informativas');
    } finally {
      setLoadingInformativeSettings(false);
    }
  };

  const saveInformativeSettings = async () => {
    setSavingInformativeSettings(true);
    try {
      await axios.post(
        `${API}/admin/informative-settings`,
        informativeSettings,
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingInformativeSettings(false);
    }
  };


  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    setAdminToken(null);
    setCurrentUsername('');
    setDashboardData(null);
    toast.success('Logout realizado com sucesso!');
  };

  if (!adminToken) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-lg p-8 rounded-2xl border border-slate-700 w-full max-w-md">
          <div className="text-center mb-8">
            <Shield className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Painel Administrativo</h1>
            <p className="text-slate-400">Acesso restrito ao administrador</p>
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Usuário</label>
              <input
                type="text"
                required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Senha</label>
              <input
                type="password"
                required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              />
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-gradient-to-r from-orange-500 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-pink-700 transition-all duration-200 disabled:opacity-50"
            >
              {loginLoading ? 'Verificando...' : 'Entrar'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Settings className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="bg-slate-900/95 backdrop-blur-lg border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-bold text-white">Painel Administrativo</h1>
            <button
              onClick={logout}
              className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Navigation Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setAdminActiveTab('dashboard')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                adminActiveTab === 'dashboard'
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setAdminActiveTab('tutorials')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                adminActiveTab === 'tutorials'
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              Upload Tutoriais
            </button>
            <button
              onClick={() => setAdminActiveTab('stripe')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                adminActiveTab === 'stripe'
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              Configurar Stripe
            </button>
            <button
              onClick={() => setAdminActiveTab('informative')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                adminActiveTab === 'informative'
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              Configurações Informativas
            </button>
            <button
              onClick={() => setAdminActiveTab('email')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                adminActiveTab === 'email'
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              Configuração de Email
            </button>
          </nav>
        </div>

        {adminActiveTab === 'dashboard' && dashboardData && (
          <>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                <div className="flex items-center space-x-3">
                  <Users className="w-8 h-8 text-blue-500" />
                  <div>
                    <h3 className="text-2xl font-bold text-white">{dashboardData.statistics.total_users}</h3>
                    <p className="text-slate-400">Total de Usuários</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                <div className="flex items-center space-x-3">
                  <Crown className="w-8 h-8 text-orange-500" />
                  <div>
                    <h3 className="text-2xl font-bold text-white">{dashboardData.statistics.active_subscriptions}</h3>
                    <p className="text-slate-400">Assinaturas Ativas</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                <div className="flex items-center space-x-3">
                  <BarChart3 className="w-8 h-8 text-green-500" />
                  <div>
                    <h3 className="text-2xl font-bold text-white">R$ {dashboardData.statistics.total_revenue.toFixed(2)}</h3>
                    <p className="text-slate-400">Receita Total</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                <div className="flex items-center space-x-3">
                  <Monitor className="w-8 h-8 text-purple-500" />
                  <div>
                    <h3 className="text-2xl font-bold text-white">{dashboardData.statistics.emby_users_count}</h3>
                    <p className="text-slate-400">Usuários Emby</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Users Management */}
            <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Gerenciar Usuários ({filteredUsers.length})</h2>
                <button
                  onClick={fetchUsers}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  disabled={loadingUsers}
                >
                  {loadingUsers ? 'Atualizando...' : 'Atualizar'}
                </button>
              </div>

              {/* Search Field */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Buscar por nome ou email..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
              </div>

              {loadingUsers ? (
                <div className="flex justify-center py-8">
                  <Settings className="w-6 h-6 text-orange-500 animate-spin" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left text-slate-400 py-3">Usuário</th>
                        <th className="text-left text-slate-400 py-3">Status / Plano</th>
                        <th className="text-left text-slate-400 py-3">Telas Extras</th>
                        <th className="text-left text-slate-400 py-3">Financeiro</th>
                        <th className="text-left text-slate-400 py-3">Datas</th>
                        <th className="text-left text-slate-400 py-3">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                          <td className="py-4">
                            <div className="flex items-start space-x-3">
                              <div className={`w-3 h-3 rounded-full mt-1 ${
                                user.subscription_status === 'active' ? 'bg-green-500' : 'bg-gray-500'
                              }`} />
                              <div>
                                <div className="text-white font-medium">{user.name}</div>
                                <div className="text-slate-400 text-xs flex items-center space-x-1">
                                  <Mail className="w-3 h-3" />
                                  <span>{user.email}</span>
                                </div>
                                {user.emby_user_id ? (
                                  <div className="text-orange-400 text-xs">Emby: {user.emby_user_id}</div>
                                ) : (
                                  <div className="text-yellow-500 text-xs flex items-center space-x-1">
                                    <span>⚠️</span>
                                    <span>Sem conta Emby</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-4">
                            <div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.subscription_status === 'active' ? 'bg-green-500/20 text-green-500' :
                                user.subscription_status === 'cancelled' ? 'bg-red-500/20 text-red-500' :
                                'bg-gray-500/20 text-gray-500'
                              }`}>
                                {user.subscription_status}
                              </span>
                              {user.subscription_plan && (
                                <div className="mt-1 text-slate-300 text-xs font-medium">
                                  {user.subscription_plan}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-4 text-slate-300">
                            <div className="text-xs space-y-1">
                              <div>Total: {1 + (user.extra_screens || 0)}</div>
                              <div className="text-slate-400 text-xs">
                                • Padrão: 1
                                {user.subscription_plan === 'combo' ? (
                                  <>
                                    <br />• Extra Canais: {user.extra_screens_channels || 0}
                                    <br />• Extra Filmes: {user.extra_screens_movies || 0}
                                  </>
                                ) : user.extra_screens > 0 ? (
                                  <><br />• Extra: {user.extra_screens}</>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="py-4">
                            <div className="text-xs space-y-1">
                              <div className="flex items-center space-x-1 text-green-400">
                                <DollarSign className="w-3 h-3" />
                                <span>R$ {user.total_paid}</span>
                              </div>
                              <div className="text-slate-400">
                                {user.total_transactions} transações
                              </div>
                              {user.last_payment_date && (
                                <div className="text-slate-400">
                                  Último: {new Date(user.last_payment_date).toLocaleDateString('pt-BR')}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-4">
                            <div className="text-xs space-y-1">
                              {user.created_at && (
                                <div className="flex items-center space-x-1 text-slate-400">
                                  <Calendar className="w-3 h-3" />
                                  <span>Criado: {new Date(user.created_at).toLocaleDateString('pt-BR')} às {new Date(user.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                              )}
                              {user.next_billing_date && (
                                <div className="flex items-center space-x-1 text-orange-400">
                                  <Clock className="w-3 h-3" />
                                  <span>Próx: {new Date(user.next_billing_date).toLocaleDateString('pt-BR')}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-4">
                            <div className="flex space-x-1">
                              {/* Botão Criar Conta Emby - NOVO - só aparece se não tiver emby_user_id */}
                              {!user.emby_user_id && (
                                <button
                                  onClick={() => createEmbyAccount(user.id, user.name, user.email)}
                                  className="p-1 text-orange-500 hover:bg-orange-500/20 rounded"
                                  title="Criar Conta Emby"
                                >
                                  <UserPlus className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => openPasswordModal(user)}
                                className="p-1 text-blue-500 hover:bg-blue-500/20 rounded"
                                title="Alterar Senha"
                              >
                                <Key className="w-4 h-4" />
                              </button>
                              {user.subscription_status === 'active' && (
                                <button
                                  onClick={() => cancelUserSubscription(user.id, user.name)}
                                  className="p-1 text-orange-500 hover:bg-orange-500/20 rounded"
                                  title="Cancelar Plano"
                                >
                                  <Ban className="w-4 h-4" />
                                </button>
                              )}
                              {user.subscription_status === 'active' ? (
                                <button
                                  onClick={() => manageUser(user.id, 'deactivate')}
                                  className="p-1 text-yellow-500 hover:bg-yellow-500/20 rounded"
                                  title="Desativar"
                                >
                                  <UserX className="w-4 h-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => manageUser(user.id, 'activate')}
                                  className="p-1 text-green-500 hover:bg-green-500/20 rounded"
                                  title="Ativar"
                                >
                                  <UserCheck className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  if (window.confirm(`Excluir permanentemente o usuário ${user.name}?\n\nEsta ação não pode ser desfeita.`)) {
                                    manageUser(user.id, 'delete');
                                  }
                                }}
                                className="p-1 text-red-500 hover:bg-red-500/20 rounded"
                                title="Excluir Permanentemente"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {filteredUsers.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      {searchTerm ? 'Nenhum usuário encontrado com este termo de busca.' : 'Nenhum usuário encontrado.'}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Admin Users Management Section - Only for svc.admin */}
            {currentUsername === 'svc.admin' && (
              <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700 mt-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-white">Usuários Administrativos ({adminUsers.length})</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={fetchAdminUsers}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      disabled={loadingAdminUsers}
                    >
                      {loadingAdminUsers ? 'Atualizando...' : 'Atualizar'}
                    </button>
                    <button
                      onClick={openCreateAdminModal}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      + Criar Admin
                    </button>
                  </div>
                </div>

                {loadingAdminUsers ? (
                  <div className="flex justify-center py-8">
                    <Settings className="w-6 h-6 text-orange-500 animate-spin" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left text-slate-400 py-3">Usuário</th>
                          <th className="text-left text-slate-400 py-3">Status</th>
                          <th className="text-left text-slate-400 py-3">Criado Por</th>
                          <th className="text-left text-slate-400 py-3">Último Login</th>
                          <th className="text-left text-slate-400 py-3">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminUsers.map((admin) => (
                          <tr key={admin.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                            <td className="py-4">
                              <div>
                                <div className="text-white font-medium">{admin.username}</div>
                                <div className="text-slate-400 text-xs flex items-center space-x-1">
                                  <Mail className="w-3 h-3" />
                                  <span>{admin.email}</span>
                                </div>
                                <div className="text-slate-500 text-xs">
                                  Criado: {new Date(admin.created_at).toLocaleDateString('pt-BR')} às {new Date(admin.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            </td>
                            <td className="py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                admin.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                              }`}>
                                {admin.is_active ? 'Ativo' : 'Inativo'}
                              </span>
                            </td>
                            <td className="py-4 text-slate-300 text-xs">
                              {admin.created_by}
                            </td>
                            <td className="py-4 text-slate-300 text-xs">
                              {admin.last_login 
                                ? `${new Date(admin.last_login).toLocaleDateString('pt-BR')} às ${new Date(admin.last_login).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                                : 'Nunca'
                              }
                            </td>
                            <td className="py-4">
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => openAdminPasswordModal(admin)}
                                  className="p-1 text-blue-500 hover:bg-blue-500/20 rounded"
                                  title="Alterar Senha"
                                >
                                  <Key className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleAdminStatusChange(admin.id, !admin.is_active)}
                                  className={`p-1 rounded ${admin.is_active 
                                    ? 'text-yellow-500 hover:bg-yellow-500/20' 
                                    : 'text-green-500 hover:bg-green-500/20'}`}
                                  title={admin.is_active ? 'Desativar' : 'Ativar'}
                                >
                                  {admin.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => handleDeleteAdmin(admin.id, admin.username)}
                                  className="p-1 text-red-500 hover:bg-red-500/20 rounded"
                                  title="Excluir Permanentemente"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {adminUsers.length === 0 && (
                      <div className="text-center py-8 text-slate-400">
                        Nenhum usuário administrativo encontrado. Crie o primeiro admin!
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Change Own Password Button - For all admins */}
            <div className="mt-6 flex justify-center">
              <button
                onClick={openOwnPasswordModal}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <Key className="w-4 h-4" />
                <span>Alterar Minha Senha</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Alterar Senha - {selectedUser.name}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Nova Senha
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Digite a nova senha (mín. 6 caracteres)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                />
              </div>

              <div className="text-xs text-slate-400">
                <p>• A senha será sincronizada automaticamente com o Emby</p>
                <p>• O usuário receberá as novas credenciais por email</p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword('');
                  setSelectedUser(null);
                }}
                className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                disabled={passwordLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleChangePassword}
                disabled={passwordLoading || !newPassword || newPassword.length < 6}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {passwordLoading ? 'Alterando...' : 'Alterar Senha'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Admin Modal */}
      {showCreateAdminModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Criar Novo Administrador
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Nome de Usuário
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Digite o nome de usuário"
                  value={newAdminData.username}
                  onChange={(e) => setNewAdminData(prev => ({ ...prev, username: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Digite o email"
                  value={newAdminData.email}
                  onChange={(e) => setNewAdminData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Senha
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Digite a senha (mín. 6 caracteres)"
                  value={newAdminData.password}
                  onChange={(e) => setNewAdminData(prev => ({ ...prev, password: e.target.value }))}
                  minLength={6}
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateAdminModal(false);
                  setNewAdminData({ username: '', email: '', password: '' });
                }}
                className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                disabled={adminPasswordLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateAdmin}
                disabled={adminPasswordLoading || !newAdminData.username || !newAdminData.email || !newAdminData.password}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {adminPasswordLoading ? 'Criando...' : 'Criar Admin'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Password Change Modal */}
      {showAdminPasswordModal && selectedAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Alterar Senha - {selectedAdmin.username}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Nova Senha
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Digite a nova senha (mín. 6 caracteres)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAdminPasswordModal(false);
                  setNewPassword('');
                  setSelectedAdmin(null);
                }}
                className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                disabled={adminPasswordLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleAdminPasswordChange}
                disabled={adminPasswordLoading || !newPassword || newPassword.length < 6}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {adminPasswordLoading ? 'Alterando...' : 'Alterar Senha'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Own Password Change Modal */}
      {showOwnPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Alterar Minha Senha
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Senha Atual
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Digite sua senha atual"
                  value={adminPasswordData.current_password}
                  onChange={(e) => setAdminPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Nova Senha
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Digite a nova senha (mín. 6 caracteres)"
                  value={adminPasswordData.new_password}
                  onChange={(e) => setAdminPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                  minLength={6}
                />
              </div>

              <div className="text-xs text-slate-400">
                {currentUsername === 'svc.admin' && (
                  <p>⚠️ Para svc.admin, é necessário contactar o administrador do sistema</p>
                )}
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowOwnPasswordModal(false);
                  setAdminPasswordData({ current_password: '', new_password: '' });
                }}
                className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                disabled={adminPasswordLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleOwnPasswordChange}
                disabled={adminPasswordLoading || !adminPasswordData.current_password || !adminPasswordData.new_password || adminPasswordData.new_password.length < 6 || currentUsername === 'svc.admin'}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {adminPasswordLoading ? 'Alterando...' : 'Alterar Senha'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Template Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-orange-500 to-pink-500 p-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-white">
                👁️ Preview do Template de Email
              </h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewTemplate(null);
                }}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-6 bg-gray-50">
              <div className="bg-white rounded-lg shadow-lg p-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ Nota:</strong> Este é um preview com dados de exemplo. O email real usará os dados do usuário e da transação.
                  </p>
                </div>
                
                <div 
                  className="email-preview"
                  dangerouslySetInnerHTML={{ __html: previewTemplate }}
                />
              </div>
            </div>
            
            <div className="bg-slate-100 p-4 flex justify-end border-t">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewTemplate(null);
                }}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all"
              >
                Fechar Preview
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Conteúdo das outras abas */}

        {adminActiveTab === 'tutorials' && <TutorialManagement />}

        {adminActiveTab === 'stripe' && <StripeConfiguration />}
        
        {adminActiveTab === 'informative' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
              <h3 className="text-xl font-bold text-white mb-6">Configurações Informativas</h3>
              
              {loadingInformativeSettings ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                  <p className="text-slate-400 mt-4">Carregando configurações...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Link do Grupo do Telegram */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Link do Grupo do Telegram
                    </label>
                    <input
                      type="url"
                      value={informativeSettings.telegram_group_link}
                      onChange={(e) => setInformativeSettings({
                        ...informativeSettings,
                        telegram_group_link: e.target.value
                      })}
                      placeholder="https://t.me/+..."
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Este link será exibido no botão que leva os usuários ao grupo do Telegram
                    </p>
                  </div>

                  {/* Informativo para Plano Filmes/Séries */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Informativo - Plano Filmes, Séries e Animes ou Combo
                    </label>
                    <textarea
                      value={informativeSettings.informative_filmes_text}
                      onChange={(e) => setInformativeSettings({
                        ...informativeSettings,
                        informative_filmes_text: e.target.value
                      })}
                      rows={10}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Este texto será exibido para usuários com Plano Combo ou Plano de Filmes/Séries
                    </p>
                  </div>

                  {/* Informativo para Plano TV */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Informativo - Plano TV (Canais)
                    </label>
                    <textarea
                      value={informativeSettings.informative_tv_text}
                      onChange={(e) => setInformativeSettings({
                        ...informativeSettings,
                        informative_tv_text: e.target.value
                      })}
                      rows={6}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Este texto será exibido para usuários com Plano Canais
                    </p>
                  </div>

                  {/* Botão Salvar */}
                  <div className="flex justify-end">
                    <button
                      onClick={saveInformativeSettings}
                      disabled={savingInformativeSettings}
                      className="px-6 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {savingInformativeSettings ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Salvando...</span>
                        </>
                      ) : (
                        <span>Salvar Configurações</span>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {adminActiveTab === 'email' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
              <h3 className="text-xl font-bold text-white mb-6">Configuração de Email</h3>
              
              {loadingEmailSettings ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                  <p className="text-slate-400 mt-4">Carregando configurações...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Tabs */}
                  <div className="border-b border-slate-700">
                    <div className="flex space-x-4">
                      <button
                        onClick={() => setActiveEmailTab('smtp')}
                        className={`py-2 px-4 border-b-2 font-medium text-sm ${
                          activeEmailTab === 'smtp'
                            ? 'border-orange-500 text-orange-500'
                            : 'border-transparent text-slate-400 hover:text-white'
                        }`}
                      >
                        Configurações SMTP
                      </button>
                      <button
                        onClick={() => setActiveEmailTab('templates')}
                        className={`py-2 px-4 border-b-2 font-medium text-sm ${
                          activeEmailTab === 'templates'
                            ? 'border-orange-500 text-orange-500'
                            : 'border-transparent text-slate-400 hover:text-white'
                        }`}
                      >
                        Templates de Email
                      </button>
                      <button
                        onClick={() => setActiveEmailTab('send')}
                        className={`py-2 px-4 border-b-2 font-medium text-sm ${
                          activeEmailTab === 'send'
                            ? 'border-orange-500 text-orange-500'
                            : 'border-transparent text-slate-400 hover:text-white'
                        }`}
                      >
                        Enviar Email
                      </button>
                    </div>
                  </div>

                  {/* Conteúdo da Tab SMTP */}
                  {activeEmailTab === 'smtp' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Servidor SMTP (Host)
                          </label>
                          <input
                            type="text"
                            value={emailSettings.smtp_host}
                            onChange={(e) => setEmailSettings({...emailSettings, smtp_host: e.target.value})}
                            placeholder="smtp.exemplo.com"
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Porta SMTP
                          </label>
                          <input
                            type="number"
                            value={emailSettings.smtp_port}
                            onChange={(e) => setEmailSettings({...emailSettings, smtp_port: parseInt(e.target.value)})}
                            placeholder="587"
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={emailSettings.smtp_use_tls}
                            onChange={(e) => setEmailSettings({...emailSettings, smtp_use_tls: e.target.checked})}
                            className="w-4 h-4 text-orange-500 bg-slate-700 border-slate-600 rounded focus:ring-orange-500"
                          />
                          <span className="text-sm text-slate-300">Usar TLS/SSL</span>
                        </label>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Usuário SMTP
                          </label>
                          <input
                            type="text"
                            value={emailSettings.smtp_username}
                            onChange={(e) => setEmailSettings({...emailSettings, smtp_username: e.target.value})}
                            placeholder="usuario@exemplo.com"
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Senha SMTP
                          </label>
                          <input
                            type="password"
                            value={emailSettings.smtp_password}
                            onChange={(e) => setEmailSettings({...emailSettings, smtp_password: e.target.value})}
                            placeholder="••••••••••"
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <p className="text-xs text-slate-400 mt-1">
                            Deixe em branco para manter a senha atual
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Email Remetente (From)
                          </label>
                          <input
                            type="email"
                            value={emailSettings.smtp_from_email}
                            onChange={(e) => setEmailSettings({...emailSettings, smtp_from_email: e.target.value})}
                            placeholder="notificacao@exemplo.com"
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Nome Remetente
                          </label>
                          <input
                            type="text"
                            value={emailSettings.smtp_from_name}
                            onChange={(e) => setEmailSettings({...emailSettings, smtp_from_name: e.target.value})}
                            placeholder="AnhomeWeb"
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Email BCC (Cópia Oculta)
                        </label>
                        <input
                          type="email"
                          value={emailSettings.bcc_email}
                          onChange={(e) => setEmailSettings({...emailSettings, bcc_email: e.target.value})}
                          placeholder="notificacao@exemplo.com"
                          className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Todos os emails enviados incluirão uma cópia oculta para este endereço
                        </p>
                      </div>

                      {/* Resultado do Teste SMTP */}
                      {smtpTestResult && (
                        <div className={`p-4 rounded-lg border ${
                          smtpTestResult.success 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : 'bg-red-500/10 border-red-500/30'
                        }`}>
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              {smtpTestResult.success ? (
                                <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              ) : (
                                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className={`font-semibold mb-1 ${
                                smtpTestResult.success ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {smtpTestResult.success ? 'Teste Bem-Sucedido!' : 'Falha no Teste'}
                              </h4>
                              <p className="text-sm text-slate-300">{smtpTestResult.message}</p>
                              {smtpTestResult.details && (
                                <div className="mt-2 text-xs text-slate-400">
                                  <p>Host: {smtpTestResult.details.host}:{smtpTestResult.details.port}</p>
                                  <p>TLS: {smtpTestResult.details.tls ? 'Ativado' : 'Desativado'}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Conteúdo da Tab Templates */}
                  {activeEmailTab === 'templates' && (
                    <div className="space-y-6">
                      <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-lg">
                        <p className="text-sm text-blue-200">
                          <strong>Nota:</strong> Os templates de email suportam HTML completo. Use variáveis como:<br/>
                          <code className="bg-slate-700 px-2 py-1 rounded">{'{user_name}'}</code>, 
                          <code className="bg-slate-700 px-2 py-1 rounded mx-1">{'{plan_name}'}</code>, 
                          <code className="bg-slate-700 px-2 py-1 rounded">{'{amount:.2f}'}</code>, 
                          <code className="bg-slate-700 px-2 py-1 rounded mx-1">{'{temp_password}'}</code>
                        </p>
                      </div>

                      {loadingTemplates ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                          <p className="text-slate-400 mt-4">Carregando templates...</p>
                        </div>
                      ) : (
                        <>
                          {/* Template de Boas-vindas */}
                          <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                            <div className="flex justify-between items-center mb-3">
                              <label className="block text-sm font-medium text-slate-300">
                                📧 Template de Boas-vindas
                              </label>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => previewEmailTemplate('welcome')}
                                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-all flex items-center space-x-1"
                                >
                                  <span>👁️ Preview</span>
                                </button>
                                <button
                                  onClick={() => saveEmailTemplate('welcome')}
                                  disabled={savingTemplate === 'welcome'}
                                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {savingTemplate === 'welcome' ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span>Salvando...</span>
                                    </>
                                  ) : (
                                    <span>💾 Salvar</span>
                                  )}
                                </button>
                                <button
                                  onClick={() => restoreEmailTemplate('welcome')}
                                  disabled={restoringTemplate === 'welcome'}
                                  className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {restoringTemplate === 'welcome' ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span>Restaurando...</span>
                                    </>
                                  ) : (
                                    <span>🔄 Restaurar Padrão</span>
                                  )}
                                </button>
                              </div>
                            </div>
                            <textarea
                              value={emailTemplates.welcome}
                              onChange={(e) => setEmailTemplates({...emailTemplates, welcome: e.target.value})}
                              rows={10}
                              placeholder="HTML do email de boas-vindas..."
                              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-xs"
                            />
                          </div>

                          {/* Template de Pagamento Confirmado */}
                          <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                            <div className="flex justify-between items-center mb-3">
                              <label className="block text-sm font-medium text-slate-300">
                                ✅ Template de Pagamento Confirmado
                              </label>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => previewEmailTemplate('payment_success')}
                                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-all flex items-center space-x-1"
                                >
                                  <span>👁️ Preview</span>
                                </button>
                                <button
                                  onClick={() => saveEmailTemplate('payment_success')}
                                  disabled={savingTemplate === 'payment_success'}
                                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {savingTemplate === 'payment_success' ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span>Salvando...</span>
                                    </>
                                  ) : (
                                    <span>💾 Salvar</span>
                                  )}
                                </button>
                                <button
                                  onClick={() => restoreEmailTemplate('payment_success')}
                                  disabled={restoringTemplate === 'payment_success'}
                                  className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {restoringTemplate === 'payment_success' ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span>Restaurando...</span>
                                    </>
                                  ) : (
                                    <span>🔄 Restaurar Padrão</span>
                                  )}
                                </button>
                              </div>
                            </div>
                            <textarea
                              value={emailTemplates.payment_success}
                              onChange={(e) => setEmailTemplates({...emailTemplates, payment_success: e.target.value})}
                              rows={10}
                              placeholder="HTML do email de pagamento confirmado..."
                              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-xs"
                            />
                          </div>

                          {/* Template de Falha no Pagamento */}
                          <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                            <div className="flex justify-between items-center mb-3">
                              <label className="block text-sm font-medium text-slate-300">
                                ⚠️ Template de Falha no Pagamento
                              </label>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => previewEmailTemplate('payment_failed')}
                                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-all flex items-center space-x-1"
                                >
                                  <span>👁️ Preview</span>
                                </button>
                                <button
                                  onClick={() => saveEmailTemplate('payment_failed')}
                                  disabled={savingTemplate === 'payment_failed'}
                                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {savingTemplate === 'payment_failed' ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span>Salvando...</span>
                                    </>
                                  ) : (
                                    <span>💾 Salvar</span>
                                  )}
                                </button>
                                <button
                                  onClick={() => restoreEmailTemplate('payment_failed')}
                                  disabled={restoringTemplate === 'payment_failed'}
                                  className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {restoringTemplate === 'payment_failed' ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span>Restaurando...</span>
                                    </>
                                  ) : (
                                    <span>🔄 Restaurar Padrão</span>
                                  )}
                                </button>
                              </div>
                            </div>
                            <textarea
                              value={emailTemplates.payment_failed}
                              onChange={(e) => setEmailTemplates({...emailTemplates, payment_failed: e.target.value})}
                              rows={10}
                              placeholder="HTML do email de falha no pagamento..."
                              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-xs"
                            />
                          </div>

                          {/* Template de Cancelamento */}
                          <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                            <div className="flex justify-between items-center mb-3">
                              <label className="block text-sm font-medium text-slate-300">
                                🚫 Template de Cancelamento
                              </label>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => previewEmailTemplate('cancellation')}
                                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-all flex items-center space-x-1"
                                >
                                  <span>👁️ Preview</span>
                                </button>
                                <button
                                  onClick={() => saveEmailTemplate('cancellation')}
                                  disabled={savingTemplate === 'cancellation'}
                                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {savingTemplate === 'cancellation' ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span>Salvando...</span>
                                    </>
                                  ) : (
                                    <span>💾 Salvar</span>
                                  )}
                                </button>
                                <button
                                  onClick={() => restoreEmailTemplate('cancellation')}
                                  disabled={restoringTemplate === 'cancellation'}
                                  className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {restoringTemplate === 'cancellation' ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span>Restaurando...</span>
                                    </>
                                  ) : (
                                    <span>🔄 Restaurar Padrão</span>
                                  )}
                                </button>
                              </div>
                            </div>
                            <textarea
                              value={emailTemplates.cancellation}
                              onChange={(e) => setEmailTemplates({...emailTemplates, cancellation: e.target.value})}
                              rows={10}
                              placeholder="HTML do email de cancelamento..."
                              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-xs"
                            />
                          </div>

                          {/* Template de Recuperação de Senha */}
                          <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                            <div className="flex justify-between items-center mb-3">
                              <label className="block text-sm font-medium text-slate-300">
                                🔐 Template de Recuperação de Senha
                              </label>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => previewEmailTemplate('password_reset')}
                                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-all flex items-center space-x-1"
                                >
                                  <span>👁️ Preview</span>
                                </button>
                                <button
                                  onClick={() => saveEmailTemplate('password_reset')}
                                  disabled={savingTemplate === 'password_reset'}
                                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {savingTemplate === 'password_reset' ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span>Salvando...</span>
                                    </>
                                  ) : (
                                    <span>💾 Salvar</span>
                                  )}
                                </button>
                                <button
                                  onClick={() => restoreEmailTemplate('password_reset')}
                                  disabled={restoringTemplate === 'password_reset'}
                                  className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {restoringTemplate === 'password_reset' ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span>Restaurando...</span>
                                    </>
                                  ) : (
                                    <span>🔄 Restaurar Padrão</span>
                                  )}
                                </button>
                              </div>
                            </div>
                            <textarea
                              value={emailTemplates.password_reset}
                              onChange={(e) => setEmailTemplates({...emailTemplates, password_reset: e.target.value})}
                              rows={10}
                              placeholder="HTML do email de recuperação de senha..."
                              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-xs"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Conteúdo da Tab Enviar Email */}
                  {activeEmailTab === 'send' && (
                    <div className="space-y-6">
                      <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-lg">
                        <p className="text-sm text-blue-200">
                          <strong>📧 Envio de Email Customizado:</strong> Selecione um usuário cadastrado ou digite um email personalizado, defina o assunto e o conteúdo HTML do email.
                        </p>
                      </div>

                      {/* Seleção de Destinatário */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Destinatário
                        </label>
                        
                        <div className="flex space-x-4 mb-3">
                          <button
                            onClick={() => setCustomEmailMode('select')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${
                              customEmailMode === 'select'
                                ? 'bg-orange-500 text-white'
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            Selecionar Usuário
                          </button>
                          <button
                            onClick={() => setCustomEmailMode('custom')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${
                              customEmailMode === 'custom'
                                ? 'bg-orange-500 text-white'
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            Email Personalizado
                          </button>
                        </div>

                        {customEmailMode === 'select' ? (
                          <select
                            value={customEmailData.recipient_email}
                            onChange={(e) => setCustomEmailData({...customEmailData, recipient_email: e.target.value})}
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                            disabled={loadingUsersEmails}
                          >
                            <option value="">Selecione um usuário...</option>
                            {usersEmails.map((user, index) => (
                              <option key={index} value={user.email}>
                                {user.name} ({user.email})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="email"
                            value={customEmailData.recipient_email}
                            onChange={(e) => setCustomEmailData({...customEmailData, recipient_email: e.target.value})}
                            placeholder="exemplo@email.com"
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        )}
                      </div>

                      {/* Campo Assunto */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Assunto
                        </label>
                        <input
                          type="text"
                          value={customEmailData.subject}
                          onChange={(e) => setCustomEmailData({...customEmailData, subject: e.target.value})}
                          placeholder="Assunto do email..."
                          className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>

                      {/* Campo Template HTML */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Conteúdo do Email (HTML)
                        </label>
                        <textarea
                          value={customEmailData.html_content}
                          onChange={(e) => setCustomEmailData({...customEmailData, html_content: e.target.value})}
                          rows={15}
                          placeholder="Cole ou digite o HTML do email aqui..."
                          className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-xs"
                        />
                      </div>

                      {/* Botões de Ação */}
                      <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700">
                        <button
                          onClick={previewCustomEmail}
                          className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>Preview</span>
                        </button>
                        
                        <button
                          onClick={sendCustomEmail}
                          disabled={sendingCustomEmail}
                          className="px-6 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          {sendingCustomEmail ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Enviando...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span>Enviar Email</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Botões - Apenas para aba SMTP */}
                  {activeEmailTab === 'smtp' && (
                    <div className="flex justify-between items-center pt-4 border-t border-slate-700">
                      <button
                        onClick={testSmtpConfiguration}
                        disabled={testingSmtp}
                        className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {testingSmtp ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Testando...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Testar Configuração</span>
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={saveEmailSettings}
                        disabled={savingEmailSettings}
                        className="px-6 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {savingEmailSettings ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Salvando...</span>
                          </>
                        ) : (
                          <span>Salvar Configurações</span>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

// Componente para gerenciamento de tutoriais
const TutorialManagement = () => {
  const [tutorials, setTutorials] = useState({ iptv: {}, filmes_series: {} });
  const [loadingTutorials, setLoadingTutorials] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const deviceTypes = [
    { key: 'mobile_android', name: 'Mobile Android', icon: '📱' },
    { key: 'mobile_apple', name: 'Mobile Apple', icon: '🍎' },
    { key: 'tv_samsung', name: 'TV Samsung', icon: '📺' },
    { key: 'tv_lg', name: 'TV LG', icon: '📺' },
    { key: 'tv_android_tv', name: 'TV Android TV', icon: '📺' },
    { key: 'tv_box', name: 'TV BOX', icon: '📦' },
    { key: 'apple_tv', name: 'Apple TV', icon: '🍎📺' },
    { key: 'fire_stick_amazon', name: 'Fire Stick Amazon', icon: '🔥' },
    { key: 'xiaomi_mi_stick', name: 'Xiaomi Mi Stick', icon: '📱📺' },
    { key: 'roku_tv', name: 'Roku TV', icon: '📺' },
    { key: 'game_xbox', name: 'Game XBOX', icon: '🎮' },
    { key: 'pc', name: 'PC', icon: '💻' }
  ];

  const fetchTutorials = async () => {
    setLoadingTutorials(true);
    try {
      const response = await axios.get(`${API}/admin/tutorials`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setTutorials(response.data.tutorials);
    } catch (error) {
      console.error('Erro ao carregar tutoriais:', error);
      toast.error('Erro ao carregar tutoriais');
    } finally {
      setLoadingTutorials(false);
    }
  };

  const handleFileUpload = async (planType, deviceType, file) => {
    if (!file || !file.type.includes('pdf')) {
      toast.error('Apenas arquivos PDF são aceitos');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploadingFile(true);
    try {
      await axios.post(
        `${API}/admin/tutorials/upload?plan_type=${planType}&device_type=${deviceType}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Tutorial enviado com sucesso!');
      fetchTutorials(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao enviar tutorial:', error);
      toast.error('Erro ao enviar tutorial');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteTutorial = async (tutorialId) => {
    if (!window.confirm('Tem certeza que deseja remover este tutorial?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/tutorials/${tutorialId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast.success('Tutorial removido com sucesso!');
      fetchTutorials(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao remover tutorial:', error);
      toast.error('Erro ao remover tutorial');
    }
  };

  useEffect(() => {
    fetchTutorials();
  }, []);

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/50 backdrop-blur-lg p-6 rounded-xl border border-slate-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white">Gerenciar Tutoriais</h2>
          <button
            onClick={fetchTutorials}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            disabled={loadingTutorials}
          >
            {loadingTutorials ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>

        {loadingTutorials ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            <p className="text-slate-400 mt-2">Carregando tutoriais...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Plano IPTV */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Monitor className="w-5 h-5 text-blue-400 mr-2" />
                Plano IPTV - Canais
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {deviceTypes.filter(d => d.key !== 'game_xbox').map(device => {
                  const tutorial = tutorials.iptv?.[device.key];
                  
                  return (
                    <div key={device.key} className="bg-slate-700/50 p-4 rounded-lg border border-slate-600">
                      <div className="text-center">
                        <div className="text-2xl mb-2">{device.icon}</div>
                        <h4 className="text-white font-medium mb-3">{device.name}</h4>
                        
                        {tutorial ? (
                          <div className="space-y-2">
                            <div className="text-xs text-slate-400">
                              {tutorial.filename}
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleDeleteTutorial(tutorial.id)}
                                className="flex-1 bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700 transition-colors"
                              >
                                Remover
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-slate-400 mb-3">
                            Nenhum tutorial
                          </div>
                        )}
                        
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleFileUpload('iptv', device.key, e.target.files[0]);
                            }
                          }}
                          className="hidden"
                          id={`iptv-${device.key}`}
                        />
                        <label
                          htmlFor={`iptv-${device.key}`}
                          className="block bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors cursor-pointer"
                        >
                          {tutorial ? 'Substituir' : 'Enviar'} PDF
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Plano Filmes, Séries */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <PlayCircle className="w-5 h-5 text-purple-400 mr-2" />
                Plano Filmes, Séries - Emby
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {deviceTypes.map(device => {
                  const tutorial = tutorials.filmes_series?.[device.key];
                  
                  return (
                    <div key={device.key} className="bg-slate-700/50 p-4 rounded-lg border border-slate-600">
                      <div className="text-center">
                        <div className="text-2xl mb-2">{device.icon}</div>
                        <h4 className="text-white font-medium mb-3">{device.name}</h4>
                        
                        {tutorial ? (
                          <div className="space-y-2">
                            <div className="text-xs text-slate-400">
                              {tutorial.filename}
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleDeleteTutorial(tutorial.id)}
                                className="flex-1 bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700 transition-colors"
                              >
                                Remover
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-slate-400 mb-3">
                            Nenhum tutorial
                          </div>
                        )}
                        
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleFileUpload('filmes_series', device.key, e.target.files[0]);
                            }
                          }}
                          className="hidden"
                          id={`filmes-${device.key}`}
                        />
                        <label
                          htmlFor={`filmes-${device.key}`}
                          className="block bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700 transition-colors cursor-pointer"
                        >
                          {tutorial ? 'Substituir' : 'Enviar'} PDF
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente de troca de cartão removido conforme solicitado

const PaymentSuccess = () => {
  const { fetchCurrentUser } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [sessionId, setSessionId] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      setSessionId(sessionId);
      checkPaymentStatus(sessionId);
    }
  }, []);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/payment-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        await fetchCurrentUser(); // Update user data
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  const getStatusContent = () => {
    switch (paymentStatus) {
      case 'checking':
        return {
          title: 'Verificando pagamento...',
          message: 'Aguarde enquanto confirmamos seu pagamento.',
          icon: <Settings className="w-16 h-16 text-blue-500 animate-spin" />,
          color: 'text-blue-500'
        };
      case 'success':
        return {
          title: 'Pagamento confirmado!',
          message: 'Sua assinatura foi ativada com sucesso. Bem-vindo ao StreamHub!',
          icon: <Check className="w-16 h-16 text-green-500" />,
          color: 'text-green-500'
        };
      case 'failed':
        return {
          title: 'Pagamento não confirmado',
          message: 'Houve um problema com seu pagamento. Tente novamente.',
          icon: <X className="w-16 h-16 text-red-500" />,
          color: 'text-red-500'
        };
      case 'timeout':
        return {
          title: 'Tempo esgotado',
          message: 'Não foi possível confirmar o pagamento. Verifique seu email para confirmação.',
          icon: <X className="w-16 h-16 text-yellow-500" />,
          color: 'text-yellow-500'
        };
      default:
        return {
          title: 'Erro',
          message: 'Ocorreu um erro inesperado. Contate o suporte.',
          icon: <X className="w-16 h-16 text-red-500" />,
          color: 'text-red-500'
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-slate-800/50 backdrop-blur-lg p-8 rounded-2xl border border-slate-700 text-center">
          <div className="mb-6">
            {statusContent.icon}
          </div>
          
          <h1 className={`text-2xl font-bold ${statusContent.color} mb-4`}>
            {statusContent.title}
          </h1>
          
          <p className="text-slate-400 mb-8">
            {statusContent.message}
          </p>

          {paymentStatus === 'success' && (
            <div className="space-y-4">
              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-gradient-to-r from-orange-500 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-pink-700 transition-all duration-200"
              >
                Ir para o Dashboard
              </button>
            </div>
          )}

          {(paymentStatus === 'failed' || paymentStatus === 'timeout' || paymentStatus === 'error') && (
            <div className="space-y-4">
              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-medium transition-colors"
              >
                Voltar ao Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/login" element={<PublicRoute><AuthForm /></PublicRoute>} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/payment/success" element={<PrivateRoute><PaymentSuccess /></PrivateRoute>} />
            <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Route Guards
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }
  
  return user ? <Navigate to="/" /> : children;
};

export default App;